import {
  GENERIC_ARTEFACT_SYMBOL_ID,
  type CollectState,
  type GambleSlotResponse,
  type HoldAndSpinResponseState,
  RR_V1_PAY_SCALE,
  SLOT_BET_MULTIPLES,
  type SpinSlotResponse,
} from '@goodtime/shared';
import { NineSliceSprite, type Container, type Texture } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { relicSymbolId, type Relic } from '../logic/collect/relics';
import { RelicsComponent, type RelicsView } from './components/RelicsComponent';
import { AudioBus } from '../audio/AudioBus';
import { SlotAudioBinding } from '../audio/SlotAudioBinding';
import type { Disposable } from '../core/Event';
import { gamePictureSymbols } from '../logic/minigames/pictures';
import { CollectLogic } from '../logic/slot/CollectLogic';
import type { RunSpinResult } from '../logic/slot/RunSpinLogic';
import { gemCellsMoment, gemsToCount, spinGems, type GemCell, type GemCellsMoment } from '../logic/slot/reelGems';
import {
  DEFAULT_HOLD_AND_SPIN_RESPINS,
  DEFAULT_HOLD_AND_SPIN_TRIGGER,
  detectBonusCells,
  detectItemDropCells,
  detectMoneyCells,
  HoldAndSpinLogic,
} from '../logic/slot/HoldAndSpinLogic';
import { RunSpinLogic } from '../logic/slot/RunSpinLogic';
import type { SlotLogicContext } from '../logic/slot/SlotLogicContext';
import { HoldAndSpinModel } from '../model/slot/HoldAndSpinModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import type {
  BoardRunState,
  BoardStopResult,
  FriendId,
  FriendInfo,
  LampGameInfo,
  PlayOffer,
  RingCellKind,
  RunPot,
  SlotTrailState,
} from '@goodtime/shared';
import { BoardRunComponent } from './components/BoardRunComponent';
import { LandmarkRoadComponent, type LandmarkRoadView } from './components/LandmarkRoadComponent';
import { SideLightsComponent } from './components/SideLightsComponent';
import { placeBetweenInsets } from '../logic/road/insets';
import type { SpinMode } from '../logic/road/boardRun';
import type { SideLightsMode } from '../widgets/road/SideLights';
import { SlotGameModel } from '../model/slot/SlotGameModel';
import { TakeWinAnimation } from '../effects/TakeWinAnimation';
import { AmbientLife } from '../widgets/slot/AmbientLife';
import { DEFAULT_THEME, type Theme } from '../theme';
import { ItemsCatalogDisplay } from '../widgets/slot/ItemsCatalogDisplay';
import { PaytableDisplay } from '../widgets/slot/PaytableDisplay';
import { SlotMachine } from '../widgets/slot/SlotMachine';
import {
  DEFAULT_PAYOUT_MAP,
  StaticPayoutTable,
  type PayoutTable,
} from '../widgets/slot/PayoutTable';
import type { SpinSettlement } from '../widgets/slot/settleSpin';
import { GemCellGlow, type GemCellGlowSnapshot } from '../widgets/slot/GemCellGlow';
import type { SlotSymbolDef, SpinOutcome } from '../widgets/slot/types';
import { STANDARD_5X3_WIN_LINES, type WinLine } from '../widgets/slot/WinLine';
import { GambleFlowComponent } from './components/GambleFlowComponent';
import type { GambleDecide } from '../widgets/slot/GambleUI';
import { HoldAndSpinComponent } from './components/HoldAndSpinComponent';
import { safeAreaInsetTop, SlotHudComponent } from './components/SlotHudComponent';
import { WinSequenceComponent } from './components/WinSequenceComponent';
import { DEFAULT_SLOT_CONFIG } from './fixtures/slotDemoFixtures';
import { BonusWinDialog } from '../widgets/slot/BonusWinDialog';
import { FindReveal, type Find } from '../widgets/slot/FindReveal';
import { LevelUpToast } from '../widgets/slot/LevelUpToast';
import { DailyBonusBoard, type DailyBonusOffer } from '../widgets/slot/DailyBonusBoard';
import { SpinHint, type SpinHintSnapshot } from '../widgets/slot/SpinHint';
import { NoticeStack, type NoticeHandle } from '../widgets/overlay/NoticeStack';
import { SettingsBoard, type SlotSettingsHost } from '../widgets/slot/SettingsBoard';
import { WalletBoard } from '../widgets/slot/WalletBoard';
import { HOLD_AND_SPIN_NAME } from '../widgets/slot/HoldAndSpinBoard';
import { SceneBase } from './SceneBase';
import type { RingArt } from '../widgets/road/BoardRing';
import { BoardDialog, loadUiFonts, TitleCard, type TitleCardOptions } from '../widgets/board/index';
import { FriendJoinDialog, type FriendJoinChoice } from '../widgets/road/FriendJoinDialog';
import type { Greeting } from '../widgets/road/FriendJoinContent';
import { lampLitCard } from '../logic/road/wheelRun';
import { HiLoDialog, type HiLoApi, type HiLoMode } from '../widgets/road/HiLoDialog';
import { RoadPanel, type RoadPanelMode } from '../widgets/road/RoadPanel';
import { TAKEN_MS } from '../widgets/road/RoadStrip';
import { trailSquareCoins } from '../logic/road/square';
import { TrailComponent } from './components/TrailComponent';
import { trailStepCoins } from '../logic/trail/trail';
import type { TrailPart } from '../widgets/trail/TrailPanel';
import { NameFlash, type NameFlashSnapshot } from '../widgets/slot/NameFlash';
import { RunBar } from '../widgets/road/RunBar';
import type { BoardRunView } from '../logic/road/boardRun';
import { ROAD_SOUNDS } from '../logic/road/sounds';
import { goldPotCard } from '../logic/road/goldPot';
import { LUCKY_RUN_NAME, RUN_NAME, lampGameOf, worldOf, type HiLoOffer, type RoadState } from '@goodtime/shared';
import { MiniGameLayer, type MiniGameEnd, type MiniGameSnapshot, type PlayApi } from '../widgets/minigames/index';
import type { SceneComponent, SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 1000;
const BET_AMOUNTS = [5, 10, 25, 50, 100] as const;
const DEFAULT_BET = 10;
const MAX_GAMBLE_STREAK = 5;
/** The background's creatures (assets/ambient), as the app registers them. */
const AMBIENT_BUTTERFLIES = ['ambient:butterfly_orange', 'ambient:butterfly_blue', 'ambient:butterfly_yellow'];
const AMBIENT_BIRD = ['ambient:bird_up', 'ambient:bird_down'] as const;
/** Target on-screen thickness of the decorative border ring on each
 * side (top/bottom/left/right). The 9-slice sprite scales uniformly
 * so the corner motifs and edge bands all render at this thickness,
 * regardless of the slot's display dimensions or the source PNG's
 * native resolution. Asset brief pack 2 §20 revised. */
const BORDER_DISPLAY_RING_PX = 50;

/** Async function the slot scene calls when running in server-authoritative
 * mode (Phase D). Resolves to the {@link SpinSlotResponse} returned from
 * {@code POST /v1/sessions/{id}/spin}. The scene converts the server's
 * symbol-id grid into the engine's {@code SpinOutcome} using the active
 * theme's symbol roster. */
export type SlotSpinFn = (bet: number) => Promise<SpinSlotResponse>;

/** A spin the server refused for a known reason. The host turns the API's
 *  409 into this so the scene can say why ({@code OUT_OF_COINS}: the free
 *  refill comes at {@code nextRefillAt}, epoch ms). */
export class SpinRefusedError extends Error {
  constructor(
    readonly code: string,
    readonly nextRefillAt: number | null = null,
  ) {
    super(`Spin refused: ${code}`);
  }
}

/** Async function the slot scene calls to gamble a server spin's win:
 *  {@code POST /v1/sessions/{id}/gamble}. The server picks the colour and
 *  moves the wallet; the scene only shows it. */
export type SlotGambleFn = (req: {
  spinId: string;
  step: number;
  guess: 'red' | 'black';
}) => Promise<GambleSlotResponse>;

const LIGHTS_FLAG = 'goodtime.road.lightsExplained';
/** Money symbols on one server spin that start hold and spin (the
 *  server's HoldAndSpinService.TRIGGER_COUNT); local play uses the
 *  engine's DEFAULT_HOLD_AND_SPIN_TRIGGER. */
const SERVER_HOLD_AND_SPIN_TRIGGER = 4;
/** Scatters anywhere that win free spins (the server's minScatterCount). */
const SCATTER_TRIGGER = 3;
const LIGHTS_EXPLAINER = `Side lights: every spin fills them a little. Fill all ten for a ${RUN_NAME}.`;
/** The sting under a bonus's title card (free spins, hold and spin). */
const BONUS_STING = 'bonus:claim';
/** How hold and spin plays, under its title card. */
function holdAndSpinIntro(spins: number): string {
  return `Coins stay locked. Each new coin resets your ${spins} spins.`;
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Storage blocked: it explains again next visit.
  }
}

/** Where the Lamp Run ring's painted pieces load from (the app registers
 *  the road ones with the road's art). */
const RING_ART: Partial<Record<RingArt, string>> = {
  bezel: 'road:light_bezel',
  lamp: 'road:lamp_lens',
  coin: 'shared:icon:coin',
  spin: 'shared:icon:spin',
  silver: 'road:pot_silver',
  bomb: 'road:bomb',
  lock: 'road:lock',
};

export interface SlotDemoSceneOptions {
  /** When provided (and {@link serverSpinAvailable} allows), spins are
   * server-authoritative — the local
   * {@link RunSpinLogic} is skipped, the wallet balance comes from the
   * server response, and the local free-spin / hold-and-spin bonuses
   * are disabled (they need server-side state machines that land in
   * Phase D.2 / D.3). */
  spinFn?: SlotSpinFn;
  /** Gambles a server spin's win on the server. Without it (or for a local
   *  spin) the gamble is drawn on the device, as offline play needs. */
  gambleFn?: SlotGambleFn;
  /** Whether {@link spinFn} can be called yet (the host's server session
   *  exists). Omitted = always. A scene that starts local switches its
   *  spins to the server once this turns true, between spins and never
   *  during a free-spin run. */
  serverSpinAvailable?: () => boolean;
  /** Fires once when a scene that started local switches to server spins. */
  onServerModeChange?: () => void;

  /** Fired once per spin, right after the player commits to a spin
   * (debit applied, reels about to start). Fires regardless of whether
   * the spin runs locally or server-authoritative. Use this to hook
   * the slot scene into multiplayer flows — e.g. submit a
   * REEL_RACE_SPIN to the room engine alongside the local visual spin.
   * Errors thrown by the callback are caught + logged so they don't
   * break the spin animation. */
  onSpinFired?: (bet: number) => void;
  /** Server mode only: fires once the reels have landed on a server spin,
   *  with the full response (the host reads extras such as the Sweet
   *  Trail from it, timed to the reels stopping rather than the request). */
  onServerSpinLanded?: (response: SpinSlotResponse) => void;
  /** True while the double-or-nothing gamble overlay is open. */
  onGambleOpenChange?: (open: boolean) => void;
  /** True while a bonus or pop-up drawn on the canvas covers the slot
   *  (hold and spin, for one): the host hides its own overlays above the
   *  canvas until it closes, so nothing draws over it. */
  onCanvasPopupChange?: (open: boolean) => void;

  /** F2.2 — onboarding-curriculum session number (UX Spec §4). When set
   * to {@code 1}, the bet ladder collapses to {@code [25]} and the BET
   * selector hides so the player's first match guarantees a non-trivial
   * payout (cheapest non-min bet). Null/undefined = standard 5-rung
   * ladder + interactive selector. */
  onboardingSession?: number | null;

  /** Phase 4 settings cog — host opens a React settings modal when
   *  the player taps the gear button. Omit to hide the gear entirely
   *  (engine-only test scenes, or hosts that have a different
   *  settings entry point). */
  onOpenSettings?: () => void;
  /** Settings the slot shows itself, on a board, with what only the host
   *  can do (notifications, restoring purchases). Without it the gear
   *  calls {@link onOpenSettings}. */
  settingsHost?: SlotSettingsHost | null;
  /** The slot celebrates the player's level-ups itself, on the canvas
   *  (the standalone slot; the host's overlays stand down). */
  celebrateOnCanvas?: boolean;

  /** When {@code false}, the gamble-on-win flow is suppressed —
   *  wins go straight to "collect" with no higher/lower offer. The
   *  multiplayer Reel Race sets this so the gamble interrupt doesn't
   *  break the racing-to-collect rhythm. Defaults to {@code true}
   *  for the standalone slot game. */
  gambleEnabled?: boolean;

  /** Fires every time the slot scene runs its layout pass with the
   *  current frame bounds (in canvas-internal renderer pixels).
   *  React overlays that need to sit alongside the Pixi-rendered HUD
   *  pills can consume these to position themselves accurately —
   *  the bounds are in the same coordinate system as the HUD's
   *  {@code machineLeft / machineRight}. Convert to CSS pixels by
   *  multiplying by {@code canvas.clientWidth / canvas.width}. */
  onLayoutBounds?: (bounds: SlotLayoutBounds) => void;

  /** When true, mount a 3-slot loadout strip inside the slot HUD
   *  row (left of the INFO/SETTINGS cluster). The host updates its
   *  state via {@link SlotDemoScene#setLoadoutSlots},
   *  {@link SlotDemoScene#setLoadoutPending}, and
   *  {@link SlotDemoScene#setLoadoutHidden}. Used by the multiplayer
   *  Reel Race; standalone slot leaves it off. */
  showLoadoutStrip?: boolean;

  /** Tile-tap handler for the loadout strip. Fires with the 0-based
   *  slot index when the player taps a tile during an active
   *  pending-claim state. The host dispatches {@code CLAIM_ITEM}
   *  with the index. No-op when {@link showLoadoutStrip} is false. */
  onLoadoutTileClick?: (slotIndex: number) => void;

  /** Producer brief 2026-05-15 — fires whenever the info modal opens
   *  or closes (paytable or items tab). The multiplayer host uses
   *  this to hide React overlays (countdown, leaderboard) that
   *  would otherwise paint above the canvas-rendered modal, since
   *  the canvas sits at zIndex 0 and the React HUD sits at zIndex
   *  10+. Boolean is {@code true} when something is open,
   *  {@code false} when both modals are closed. */
  onInfoModalToggle?: (open: boolean) => void;

  /** Slot mastery (M1, 2026-06-26) — the stringified {@code WorldMapNode.id}
   *  of the slot being played. When set (alongside a profile), each paid
   *  spin's bet is recorded against this slot via
   *  {@link PlayerProfile#recordWager} so its cumulative-wagered stars /
   *  medal fill. Null on engine-only / multiplayer scenes that don't map
   *  to a single-player world node. */
  slotNodeId?: string | null;

  /** The slot's bet B (Decision 2: every slot on one maths). The BET pill
   *  offers B times {@link SLOT_BET_MULTIPLES}, starting at the multiple the
   *  player last chose, and the paytable shows the server's pay scale. Null
   *  keeps the old bets (the Reel Race, locked). */
  baseBet?: number | null;

  /** Royal Road (a flagged player on a road slot): no gamble
   *  offer, side lights on the frame, and Lamp Runs over the reels. */
  road?: boolean;

  /** Royal Road: SPIN (or Space) pressed while it reads STOP. */
  onBoardStopPressed?: () => void;

  /** Royal Road: SPIN (or Space) pressed while it reads COLLECT or DONE. */
  onBoardCollectPressed?: () => void;

  /** Fires when a spin has fully finished (reels, wins and bonuses) and the
   *  slot is waiting for SPIN again. Royal Road starts a Lamp Run here, so
   *  it never cuts across a win. */
  onIdle?: () => void;
}

/** Slot-scene frame bounds — emitted on each layout pass via
 *  {@link SlotDemoSceneOptions#onLayoutBounds}. All values are in
 *  the same canvas-internal-pixel coordinate space the HUD uses;
 *  React consumers convert to CSS via {@code clientWidth / width}. */
export interface SlotLayoutBounds {
  /** Renderer's internal width — divisor for the canvas-to-CSS ratio. */
  rendererWidth: number;
  /** Renderer's internal height — divisor for the canvas-to-CSS ratio. */
  rendererHeight: number;
  /** Slot frame's left edge (the wood-frame painted box). HUD's
   *  BET pill anchors here on the left side of the row. */
  machineLeft: number;
  /** Slot frame's right edge — HUD's INFO/SETTINGS cluster pill
   *  anchors here. Layouts aligned to the LEFT of the cluster
   *  (such as the wheel-game's loadout strip) compute their right
   *  edge as {@code machineRight - cluster_width - gap}. */
  machineRight: number;
  /** Slot frame's top edge. */
  machineTop: number;
  /** Slot frame's bottom edge. */
  machineBottom: number;
  /** Y-coordinate of the BET/SPIN/INFO row centreline. */
  spinButtonY: number;
}

/** Gem cells: how long a cell shows its "+N" before its gems lift off, and
 *  how long the first of them take to reach the wallet. */
const GEM_LIFT_MS = 250;
const GEM_FLY_MS = 700;

/**
 * SlotDemoScene — the slot game scene. Owns the {@link SlotMachine} and
 * runs the master state machine (spin → settle → highlight → offer-or-collect
 * → idle), composing three UI components: {@link SlotHudComponent} (chrome),
 * {@link GambleFlowComponent} (gamble overlay), {@link WinSequenceComponent}
 * (win celebration).
 *
 * <p>Pixi app lifecycle + the strict-mode-unmount race come from
 * {@link SceneBase}. Symbol/config fixtures live in
 * {@link './fixtures/slotDemoFixtures'} so a host can swap them without
 * touching this orchestration.
 */
export class SlotDemoScene extends SceneBase {
  private machine: SlotMachine | null = null;
  private borderSprite: NineSliceSprite | null = null;
  private takeWin: TakeWinAnimation | null = null;
  /** The gem cells' glow and "+N" over the reels, just above them. */
  private gemGlow: GemCellGlow | null = null;
  /** The last gem cells' moment: the cells and the gems it counted. */
  private lastGemCells: { cells: GemCell[]; counted: number } | null = null;
  private titleCard: TitleCard | null = null;
  /** Royal Road's chrome on the canvas (producer 2026-10-14: everything in
   *  the game is drawn in Pixi): the panel, the run bar and Hi-Lo. */
  private roadPanel: RoadPanel | null = null;
  private runBar: RunBar | null = null;
  private hiloDialog: HiLoDialog | null = null;
  private roadChrome: { mode: RoadPanelMode; top: number; height: number; maxWidth: number } | null = null;
  private roadChromeHidden = false;
  private roadExpanded = false;
  /** The Sweet Trail on a slot without the road (made on first use). */
  private trailChrome: TrailComponent | null = null;
  /** The slot's name, flashed as it opens (made on first use). */
  private nameFlash: NameFlash | null = null;
  private runShown = false;
  private lastCtx: SceneLayoutContext | null = null;
  /** The road's art, loading in the background (see preload). */
  private roadArt: Promise<void> | null = null;
  /** A run's title card; a STOP waits for it to finish. */
  private runIntro: Promise<void> = Promise.resolve();
  private lightsExplained = false;
  /** What the host has said about the road so far. It can arrive before the
   *  scene has built its panel (the host gets the scene at construction), so
   *  the panel picks it up when it's built. */
  private roadNow: { road: RoadState | null; spinKey: number } = { road: null, spinKey: 0 };
  /** Coins the road's trail squares paid on the last spin that haven't
   *  reached the coin counter yet. The spin's wallet counts them already,
   *  so the balance shows them only as they fly in from the road. */
  private roadCoinsOwed = 0;
  /** Changes with each spin (and when the balance is taken from the server
   *  again): a flight from before stops adding its coins. */
  private roadPayKey = 0;
  private roadPayTimer: ReturnType<typeof setTimeout> | null = null;
  /** The last trail-square flight, for browser tests. */
  private roadPrizeFly: { coins: number; from: { x: number; y: number } } | null = null;
  private runNow: { view: BoardRunView | null; status: string; notice: boolean } = { view: null, status: '', notice: false };
  private friendJoin: FriendJoinDialog | null = null;
  /** The host starts a Wheel Run lamp's free first game (the player chose
   *  PLAY FREE on its card, or tapped its lamp). */
  onLampFreePlay: ((lamp: RingCellKind) => void) | null = null;
  /** The host opens the gift square's gift (a tap on the road while it waits). */
  onRoadGiftTap: (() => void) | null = null;
  /** The host starts a friend's free first game (the player chose PLAY FREE
   *  from their lamp). */
  onFriendFreePlay: ((friend: FriendId) => void) | null = null;
  private hud: SlotHudComponent | null = null;
  private gambleFlow: GambleFlowComponent | null = null;
  private winSequence: WinSequenceComponent | null = null;
  private paytable: PaytableDisplay | null = null;
  /** Multiplayer-only items catalog modal — paired with
   *  {@link paytable} as a second info-button page. Constructed
   *  only when {@code showLoadoutStrip} is true (the existing
   *  multiplayer signal). Tap the info button cycles paytable →
   *  items → off when this exists; falls back to a normal toggle
   *  when it doesn't. */
  private itemsCatalog: ItemsCatalogDisplay | null = null;
  /** The end of the hold-and-spin bonus: what it paid, and COLLECT. */
  private bonusWinDialog: BonusWinDialog | null = null;
  private holdAndSpinComponent: HoldAndSpinComponent | null = null;
  /** Royal Road: side lights on the frame and the Lamp Run board. */
  private sideLights: SideLightsComponent | null = null;
  private boardRun: BoardRunComponent | null = null;
  /** Phase 3: the story's layer (Fen, beats, cards), when mounted. */
  private storyLayer: (Container & SceneComponent) | null = null;
  /** A story layer handed over before the scene was built: mounted once it
   *  is, so it sits over the slot rather than under its background. */
  private storyWaiting: (Container & SceneComponent) | null = null;
  private contentBuilt = false;
  private markBuilt: () => void = () => {};
  private readonly builtPromise = new Promise<void>((resolve) => (this.markBuilt = resolve));
  private lastLayout: SceneLayoutContext | null = null;
  /** Room the host keeps above and below the reels (the Royal Road panel
   *  and trail); null uses the standard layout. */
  private chromeInsets: { top: number; bottom: number } | null = null;
  /** Side-light state set before the scene finished building. */
  private pendingSideLights: { lit: number; next: number } | null = null;
  /** Phase 2a: the road's landmarks under the SPIN row. */
  private landmarkRoad: LandmarkRoadComponent | null = null;
  /** Butterflies, birds and drifting light in the background, if the
   *  theme has any. */
  private ambientLife: AmbientLife | null = null;
  private pendingLandmarkRoad: LandmarkRoadView | null = null;
  /** Reel collectibles (reel_collect): the relic shelf and its moments. */
  private relics: RelicsComponent | null = null;
  private pendingRelics: RelicsView | null = null;
  /** What the spin in flight found among the relics, played once it lands. */
  private pendingCollect: CollectState | null = null;
  private readonly relicSymbols = new Map<string, SlotSymbolDef>();
  private readonly road: boolean;
  private readonly onBoardStopPressed: (() => void) | null;
  private readonly onBoardCollectPressed: (() => void) | null;
  private readonly onIdle: (() => void) | null;
  private spinning = false;
  /** Queued item-drop reveal. The host calls
   *  {@link playItemDropReveal} on the SSE delta the moment it sees a
   *  loadout-growth + {@code lastSpinItemCell}; that arrives WHILE the
   *  reels are still spinning visually. Firing the cycle animation now
   *  would compete with the strip's own symbol-scroll swaps and read
   *  as a flicker. Instead, we stash the reveal here and consume it
   *  inside the post-spin handler (where the bonus-collect pulse also
   *  fires), so the cycle starts cleanly with the reel idle. The
   *  promise resolves when the cycle+settle completes — the host
   *  awaits it before triggering its post-reveal chip-fly. */
  private pendingItemDropReveal:
    | {
        cell: number;
        finalDef: SlotSymbolDef;
        cycleDefs: readonly SlotSymbolDef[];
        resolve: () => void;
      }
    | null = null;
  /** Stage-coord positions of every cell that contributed to the most
   *  recently landed win (across all settlements + scatter). Captured by
   *  {@link handleSpin} the moment a win settles, consumed by
   *  {@link runCollect} so the take-win flying-coin animation spawns
   *  coins from EACH winning symbol rather than a single burst centre.
   *  Empty after no-win spins or after the celebration completes. */
  private lastWinningCellPositions: Array<{ x: number; y: number }> = [];

  private readonly model: SlotGameModel;
  private readonly winLines: readonly WinLine[];
  private readonly payoutTable: PayoutTable;

  // Phase B.2: domain operations live behind named Logic classes. Stateless
  // beyond their context — multiple spins reuse the same instance.
  private runSpinLogic: RunSpinLogic | null = null;
  private collectLogic: CollectLogic | null = null;
  private holdAndSpinLogic: HoldAndSpinLogic | null = null;

  // Phase C.3: audio binding subscribes to model events and plays SFX.
  // Constructed in buildContent, disposed in disposeResources.
  private audioBinding: SlotAudioBinding | null = null;

  // Phase G.1: shared player wallet. When provided, model balance changes
  // propagate to the profile so other scenes see the same balance.
  private profileSubscription: Disposable | null = null;
  /** Profile → model: see {@link followProfileBalance}. */
  private profileBalanceSubscription: Disposable | null = null;
  /** Hides a waiting GAMBLE offer when the player switches gamble off
   *  in Settings mid-session. */
  private gamblePrefSubscription: Disposable | null = null;
  /** Reference to the same profile passed into the constructor, kept on
   *  the instance so {@link handleSpin} can credit gem-on-reel
   *  ({@link SlotSymbolDef#gemValue}) totals after each settle. Null
   *  for headless / engine-only test scenes. */
  private readonly profile: PlayerProfile | null;

  /** Phase D.1 — server-authoritative spin function. Null means
   * local-only (offline / pre-server). When set, {@link handleSpin}
   * routes through {@link runServerSpin} instead of {@link RunSpinLogic}. */
  private readonly spinFn: SlotSpinFn | null;
  private readonly gambleFn: SlotGambleFn | null;
  /** See {@link SlotDemoSceneOptions.baseBet}. */
  private readonly baseBet: number | null;
  /** The latest server spin; its win is the one a gamble stakes. Null
   *  after a local spin. */
  private lastServerSpinId: string | null = null;
  private readonly serverSpinAvailable: (() => boolean) | null;
  private readonly onServerModeChange: (() => void) | null;
  /** True once spins go to {@link spinFn}. Latches: a scene never drops
   *  back to local spins. */
  private serverSpins: boolean;

  /** Optional side-channel hook fired once per spin commit. Used by
   * the multiplayer Reel Race to submit a REEL_RACE_SPIN alongside
   * the local visual spin. */
  private readonly onSpinFired: ((bet: number) => void) | null;
  private readonly onServerSpinLanded: ((response: SpinSlotResponse) => void) | null;
  private readonly onGambleOpenChange: ((open: boolean) => void) | null;
  private readonly onCanvasPopupChange: ((open: boolean) => void) | null;
  /** True while the hold-and-spin board has the road panel's place. */
  private bonusShown = false;
  /** Lets the slot go again when the bonus ends (see {@link coverSlot}). */
  private bonusCover: (() => void) | null = null;
  /** How many canvas pop-ups, title cards and bonuses cover the slot now. */
  private covers = 0;

  /** Phase D.3 — set by {@link runServerSpin} when the trigger spin's
   * response carries {@code holdAndSpin.phase === 'started'}. Drives
   * {@link handleSpin} to call {@link runServerBonusFlow} instead of
   * the local {@link runHoldAndSpinBonus}. Cleared once consumed. */
  private pendingServerBonus: HoldAndSpinResponseState | null = null;

  /** F2.2 — onboarding session number, forwarded to {@link SlotHudComponent}
   * so it can hide the BET selector when locked. Null means no override. */
  private readonly onboardingSession: number | null;

  /** Phase 4 settings cog — invoked when the player taps the gear in
   *  the HUD. Null = no gear shown (engine-only test scenes). */
  private readonly onOpenSettings: (() => void) | null;
  private readonly settingsHost: SlotSettingsHost | null;
  private readonly celebrateOnCanvas: boolean;
  /** The slot's own pop-ups, made when first needed. */
  private settingsBoard: SettingsBoard | null = null;
  private walletBoard: WalletBoard | null = null;
  /** A new player's "Tap SPIN to play" (made on first use). */
  private spinHint: SpinHint | null = null;
  private spinHintSlot: NoticeHandle | null = null;
  private dailyBoard: DailyBonusBoard | null = null;
  private findReveal: FindReveal | null = null;
  private levelToast: LevelUpToast | null = null;
  private profileSubscriptions: Disposable[] = [];

  /** Layout-bounds emitter. Fired on each layout pass so React
   *  overlays can position themselves alongside the Pixi HUD. */
  private readonly onLayoutBounds: ((b: SlotLayoutBounds) => void) | null;

  /** Multiplayer loadout strip — mounted inside the slot HUD when
   *  {@link SlotDemoSceneOptions#showLoadoutStrip} is set. */
  private readonly showLoadoutStrip: boolean;
  /** Tile-tap handler forwarded to the loadout strip's
   *  {@code onTileClick}. */
  private readonly onLoadoutTileClick:
    | ((slotIndex: number) => void)
    | null;

  /** Whether the gamble-on-win flow is wired. {@code false} on the
   *  multiplayer Reel Race so gambling doesn't interrupt the
   *  race-to-collect rhythm. */
  private readonly gambleEnabled: boolean;

  /** Bridge to the React host — fires whenever the info modal (the
   *  paytable / items dialog) opens or closes so React overlays
   *  above the canvas (countdown, leaderboard) can hide for the
   *  duration. Producer brief 2026-05-15. */
  private readonly onInfoModalToggle: ((open: boolean) => void) | null;

  /** Slot mastery (M1) — world-node id this slot maps to, for per-slot
   *  wagered tracking. Null on engine-only / multiplayer scenes. */
  private readonly slotNodeId: string | null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
    options: SlotDemoSceneOptions = {},
  ) {
    super(container, { theme });
    this.profile = profile;
    this.winLines = STANDARD_5X3_WIN_LINES;
    this.payoutTable = new StaticPayoutTable(DEFAULT_PAYOUT_MAP);
    this.onboardingSession = options.onboardingSession ?? null;
    // F2.2 — session 1 collapses the bet ladder to a single rung at 25.
    // BetSelector.cycle() is already a no-op on length<=1; clampBetToAffordable
    // can only ever pick 25; setBet(25) round-trips through the same value.
    // Together this makes the lock structurally tight against any code path
    // that might otherwise lower the bet behind the HUD.
    const isSessionOne = this.onboardingSession === 1;
    this.baseBet = options.baseBet ?? null;
    const base = this.baseBet;
    const betAmounts =
      isSessionOne ? [base ?? 25]
      : base !== null ? SLOT_BET_MULTIPLES.map((m) => m * base)
      : [...BET_AMOUNTS];
    // The player's last chosen multiple, on any slot.
    const remembered = base !== null ? base * (profile?.slotBetMultiple ?? 1) : DEFAULT_BET;
    this.model = new SlotGameModel({
      startingBalance: profile?.balance ?? STARTING_BALANCE,
      betAmounts,
      initialBet: betAmounts.includes(remembered) ? remembered : betAmounts[0]!,
      maxGambleStreak: MAX_GAMBLE_STREAK,
    });
    this.slotNodeId = options.slotNodeId ?? null;
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', (e) => {
        // Slot mastery (M1) — a 'spin-bet' debit is a wager. Record it
        // against this slot so its cumulative-wagered stars fill. Only
        // the bet leg counts: credits ('collect') and refills
        // ('session-grant') are not wagers, and free spins never debit.
        if (this.slotNodeId && e.reason === 'spin-bet' && e.after < e.before) {
          profile.recordWager(this.slotNodeId, e.before - e.after);
        }
        profile.setBalance(this.model.balance);
        // If the profile auto-reloaded a busted balance (zero-balance
        // refill), the profile is now ahead of the model — sync the
        // model back up so subsequent credits land on top of the
        // refilled balance instead of on the busted zero. The next
        // model 'balance-changed' will be a no-op on the profile
        // (already at the same value), so this doesn't loop. Without
        // this back-sync, the model would carry on at 0 and overwrite
        // the profile's refilled 1000 on the very next setBalance.
        if (profile.balance !== this.model.balance) {
          this.model.setBalance(profile.balance, 'session-grant');
        }
      });
      this.profileBalanceSubscription = profile.events.on('balance-changed', () =>
        this.followProfileBalance(),
      );
      this.gamblePrefSubscription = profile.events.on('gamble-pref-changed', (e) => {
        // The waiting win still collects on the next SPIN tap.
        if (!e.after) this.hud?.setGambleButtonVisible(false);
      });
    }
    this.spinFn = options.spinFn ?? null;
    this.gambleFn = options.gambleFn ?? null;
    this.serverSpinAvailable = options.serverSpinAvailable ?? null;
    this.onServerModeChange = options.onServerModeChange ?? null;
    this.serverSpins = this.spinFn !== null && (this.serverSpinAvailable?.() ?? true);
    this.onSpinFired = options.onSpinFired ?? null;
    this.onServerSpinLanded = options.onServerSpinLanded ?? null;
    this.onGambleOpenChange = options.onGambleOpenChange ?? null;
    this.onCanvasPopupChange = options.onCanvasPopupChange ?? null;
    this.onOpenSettings = options.onOpenSettings ?? null;
    this.settingsHost = options.settingsHost ?? null;
    this.celebrateOnCanvas = options.celebrateOnCanvas === true;
    this.onLayoutBounds = options.onLayoutBounds ?? null;
    this.showLoadoutStrip = options.showLoadoutStrip === true;
    this.onLoadoutTileClick = options.onLoadoutTileClick ?? null;
    this.road = options.road === true;
    // A road slot has no gamble offer (Hi-Lo replaces it).
    this.gambleEnabled = (options.gambleEnabled ?? true) && !this.road;
    this.onBoardStopPressed = options.onBoardStopPressed ?? null;
    this.onBoardCollectPressed = options.onBoardCollectPressed ?? null;
    this.onIdle = options.onIdle ?? null;
    this.onInfoModalToggle = options.onInfoModalToggle ?? null;
  }

  protected async preload(): Promise<void> {
    // Only the first-spin textures block the scene. The rest of the theme
    // (win banners, per-symbol win art, wheel + bonus art) and all of its
    // audio start once the scene is built (end of buildContent), so they
    // don't compete with the renderer code Pixi fetches during app.init.
    // Sounds and music asked for before they've decoded are queued by
    // AudioBus and play once ready.
    // The road's art (side-light bezels, ring, run buttons) starts alongside
    // and doesn't block: each piece is looked up when it's first drawn.
    if (this.road) this.roadArt = AssetLoader.shared.loadThemeRoad(this.theme);
    // Pixi draws text once, so the game's fonts load before anything is built.
    await Promise.all([AssetLoader.shared.loadThemeEssentials(this.theme), loadUiFonts()]);
    // Start the biome's looping music + ambient bed if the theme
    // declares them. Calls before first user gesture are queued by
    // AudioBus and started automatically on the next resume(), so
    // wiring this here (vs. on first SPIN tap) keeps the scene's
    // boot path simple. Theme switches cross-fade via the same path
    // — AudioBus.playMusic detects the alias change and ramps the
    // outgoing track down while the new one fades in.
    if (this.theme.musicAlias) {
      AudioBus.shared.playMusic(this.theme.musicAlias, { fadeInMs: 500 });
    }
    if (this.theme.ambientAlias) {
      AudioBus.shared.playAmbient(this.theme.ambientAlias, { fadeInMs: 500 });
    }
  }

  protected buildContent(): void {
    // When the theme ships a border overlay, drop the slot's
    // internal frame-pad gutter to zero — the wooden / ice / etc.
    // border IS the frame. Border lives OUTSIDE the slot machine
    // (positioned in {@link layout}), so its painted ring's inner
    // edge butts directly against the reel grid's outer edge with
    // no gap and no nested padding.
    const hasBorder = !!this.theme.assets?.scene?.border;
    const framePad = hasBorder ? 0 : undefined;
    this.machine = new SlotMachine(this.app.ticker, {
      ...DEFAULT_SLOT_CONFIG,
      symbols: this.theme.slot.symbols,
      chrome: {
        accentColor: this.theme.palette.accent,
        frameFillColor: this.theme.palette.frameFill,
        columnBackgroundAlias: this.theme.assets?.scene?.columnBackground?.alias,
        framePad,
        // The wooden / ice / timber border IS the frame — skip the
        // engine's default rounded-rect fill so we don't double-frame.
        omitInnerFrame: hasBorder,
      },
    });
    // The background's life (butterflies, birds, drifting light) sits on
    // the background, under the reels and everything else.
    const life = this.theme.ambientLife;
    if (life) {
      const loader = AssetLoader.shared;
      this.ambientLife = new AmbientLife(life, {
        butterflies: () =>
          AMBIENT_BUTTERFLIES.map((a) => loader.getTexture(a)).filter((t): t is Texture => t !== null),
        bird: () => {
          const up = loader.getTexture(AMBIENT_BIRD[0]);
          const down = loader.getTexture(AMBIENT_BIRD[1]);
          return up && down ? [up, down] : null;
        },
        // The road panel or run bar above the reels, while one shows.
        cover: () => {
          const panel = this.runBar?.visible ? this.runBar : this.roadPanel?.visible ? this.roadPanel : null;
          if (!panel) return null;
          const b = panel.getBounds();
          return { left: b.x, right: b.x + b.width, bottom: b.y + b.height };
        },
      });
      if (this.ambientLife.enabled) {
        this.app.stage.addChild(this.ambientLife);
        const ambient = this.ambientLife;
        this.app.ticker.add((ticker) => {
          if (!ambient.destroyed) ambient.tick(ticker.deltaMS);
        });
      }
    }
    this.app.stage.addChild(this.machine);

    // Decorative 9-slice border that wraps the machine — corners stay
    // native, edges stretch to fit the 5:3 reel grid. See asset brief
    // pack 2 §20. Sized + positioned in {@link layout} once the
    // machine's actual size is known.
    this.borderSprite = this.buildBorderSprite();
    if (this.borderSprite) {
      this.app.stage.addChild(this.borderSprite);
    }

    this.takeWin = new TakeWinAnimation(this.app.ticker);
    this.app.stage.addChild(this.takeWin);

    // The +N popup is gold display type on a wooden plate, readable over
    // any theme and any tier's headline art.
    this.winSequence = new WinSequenceComponent(this.app.ticker, {
      theme: this.theme,
    });
    this.app.stage.addChild(this.winSequence);

    this.hud = new SlotHudComponent(
      this.model,
      {
        onSpin: () => void this.handleSpin(),
        onTogglePaytable: () => this.cycleInfoModal(),
        onOpenSettings: this.onOpenSettings || this.settingsHost ? () => void this.openSettings() : undefined,
        // 2026-06-04 — inline GAMBLE button on the HUD replaces the
        // old modal-style GAMBLE/COLLECT dual buttons. Only wire it
        // when the scene supports gamble at all (multiplayer Reel
        // Race uses gambleEnabled=false to skip; in that case the
        // HUD never builds the medallion).
        onGamble: this.gambleEnabled ? () => this.handleInlineGamble() : undefined,
        onBetChosen: (bet) => {
          if (this.baseBet !== null) this.profile?.setSlotBetMultiple(bet / this.baseBet);
        },
      },
      {
        freeSpinChipAccent: this.theme.palette.accent,
        // Chip glyph mirrors the theme's scatter glyph so the visual
        // language is "this is the symbol that triggered me".
        freeSpinChipGlyph: this.scatterGlyph(),
        onboardingSession: this.onboardingSession,
        // Multiplayer Reel Race spins are server-side locked to a
        // fixed notional bet — paint the padlock badge so the player
        // reads "not tappable" at a glance. {@code gambleEnabled ===
        // false} is the multiplayer signal (the wheel embed sets it
        // to suppress the gamble-on-win interrupt); standalone slot
        // leaves the BET pill tappable. Producer brief 2026-05-12:
        // "the single player games should be the standard coin and
        // allow the user to adjust the bet."
        // A slot on the server's maths (baseBet) always offers its ladder,
        // road slots included (they have no gamble either).
        lockBet: this.baseBet === null && !this.gambleEnabled,
        // Producer audit Phase 4 (slot-HUD redesign brief) — the
        // wallet HUD now lives in a persistent React {@code
        // BalancePill} overlay mounted by App.tsx. Hide the Pixi-side
        // balance counter unconditionally (both standalone + the
        // REEL_RACE wheel embed). Keep the free-spin chip visible in
        // standalone mode (no other layer surfaces bonus-spin counts);
        // the wheel embed continues to hide both since the wheel
        // header owns its own scoring readout.
        hideHudBalance: true,
        keepFreeSpinChip: !this.serverSpins,
        // Multiplayer Reel Race mounts the loadout strip inside the
        // HUD row so its chrome matches the gear/info cluster pill
        // exactly. The standalone slot stays untouched.
        showLoadoutStrip: this.showLoadoutStrip,
      },
    );
    this.app.stage.addChild(this.hud);
    if (this.hud.loadoutStrip && this.onLoadoutTileClick) {
      this.hud.loadoutStrip.onTileClick = this.onLoadoutTileClick;
    }
    // Re-stack the Big Win celebration (flying coins + win popup) ABOVE the
    // HUD. Stage children draw in insertion order and the HUD is mounted
    // last (for input layering), so its top row — the Reel Race loadout
    // strip — was painting over the Big Win popup (producer 2026-06-10).
    // addChild on an already-parented node just moves it to the top; later
    // modals (paytable, gamble, hold-and-spin) still add after these and
    // stay above the celebration.
    if (this.takeWin) this.app.stage.addChild(this.takeWin);
    if (this.winSequence) this.app.stage.addChild(this.winSequence);
    if (this.hud.loadoutStrip) {
      // Drive the strip's internal animations (fade, pulse, bob)
      // off the same ticker the slot scene uses for everything else.
      // {@code deltaMS} is the frame interval in milliseconds — Pixi
      // supplies it directly on the ticker.
      const strip = this.hud.loadoutStrip;
      this.app.ticker.add((ticker) => {
        strip.tick(ticker.deltaMS);
      });
    }

    // Multiplayer Reel Race opts out of the gamble overlay so wins
    // collect immediately — the gamble interrupt would slow the
    // race-to-collect rhythm. gambleFlow stays null in that mode;
    // the downstream callers null-check (the existing pattern).
    if (this.gambleEnabled) {
      this.gambleFlow = new GambleFlowComponent(this.model, MAX_GAMBLE_STREAK, {
        setResultText: (text) => this.hud?.setResultText(text),
        collect: () => this.runCollect(),
        onExit: () => this.exitToIdle(),
        onGambleOpenChange: (open) => this.onGambleOpenChange?.(open),
        decider: () => this.serverGamble(),
        floor: () => {
          const alias = this.theme.assets?.scene?.columnBackground?.alias;
          return alias ? AssetLoader.shared.getTexture(alias) : null;
        },
      });
      this.app.stage.addChild(this.gambleFlow);
    }

    // Paytable symbol filter (producer 2026-06-08):
    //  • Gems are dropped from the paytable everywhere ("remove the gems
    //    from the pay table") — they still land + award, surfaced via the
    //    gem-drop toast, just not listed as a paytable row.
    //  • Trophy/artefact symbols are a single-player-only collectible, so
    //    they're also dropped from the multiplayer Reel Race paytable
    //    (showLoadoutStrip) — not obtainable there.
    const paytableSymbols = this.theme.slot.symbols.filter((s) => {
      if (s.gemValue !== undefined && s.gemValue > 0) return false;
      if (this.showLoadoutStrip && s.artefactId) return false;
      return true;
    });
    const floorArt = () => {
      const alias = this.theme.assets?.scene?.columnBackground?.alias;
      return alias ? AssetLoader.shared.getTexture(alias) : null;
    };
    this.paytable = new PaytableDisplay(
      paytableSymbols,
      this.payoutTable,
      this.model.currentBet,
      this.theme.slot.wildSymbolId,
      this.theme.slot.scatterSymbolId ?? null,
      () => ({
        holdAndSpinTrigger: this.serverSpins ? SERVER_HOLD_AND_SPIN_TRIGGER : DEFAULT_HOLD_AND_SPIN_TRIGGER,
        scatterTrigger: SCATTER_TRIGGER,
      }),
      floorArt,
    );
    if (this.baseBet !== null) this.paytable.setPayScale(RR_V1_PAY_SCALE);
    this.app.stage.addChild(this.paytable);
    // Closing it from its own CLOSE or the dim lets the slot go again.
    this.paytable.onCloseRequested = () => this.infoClosed();

    // Producer brief 2026-05-14 — multiplayer Reel Race gets a
    // second info-button page listing the wheel-game item effects.
    // Same construction signal as the loadout strip: this is the
    // multiplayer slot embed. Standalone slot leaves the catalog
    // off so the info button cycles back to its single-toggle
    // behaviour.
    if (this.showLoadoutStrip) {
      this.itemsCatalog = new ItemsCatalogDisplay(floorArt);
      this.app.stage.addChild(this.itemsCatalog);
      // Producer brief 2026-05-15 — multiplayer info dialog presents
      // the slot paytable and the wheel item catalog as two tabs in
      // the same modal. Wire both dialogs with mirror tab lists; the
      // inactive tab's onTap swaps which dialog is visible. The dim
      // tap-to-close on either dialog kills both, and the info button
      // (see {@link cycleInfoModal}) just opens/closes the modal
      // without cycling.
      this.configureMultiplayerTabs();
    }

    // Producer audit Phase 4 — keep the paytable's payout column
    // accurate as the player toggles the BetSelector. The model fires
    // {@code bet-changed} on every BET cycle (and on clamp-to-affordable);
    // we forward it straight to {@link PaytableDisplay#setBet}, which
    // refreshes the per-row ×3 / ×4 / ×5 winnings in place.
    this.model.events.on('bet-changed', (event) => {
      this.paytable?.setBet(event.after);
    });

    // Hold and spin: its coins ride on the reels (a layer inside the
    // machine) and its board sits at scene level, restacked over the road
    // chrome below; the end board and title card cover both.
    const floor = () => {
      const alias = this.theme.assets?.scene?.columnBackground?.alias;
      return alias ? AssetLoader.shared.getTexture(alias) : null;
    };
    const coin = () => AssetLoader.shared.getTexture('shared:icon:coin');
    this.holdAndSpinComponent = new HoldAndSpinComponent(this.machine, {
      floor,
      bezel: () => AssetLoader.shared.getTexture('road:light_bezel'),
      lens: () => AssetLoader.shared.getTexture('road:lamp_lens'),
      coin,
    });
    this.app.stage.addChild(this.holdAndSpinComponent);
    this.bonusWinDialog = new BonusWinDialog({ floor, coin });

    // Royal Road: side lights and the Lamp Run board sit over the reels
    // but under the HUD, so the SPIN button (STOP / COLLECT) stays on top.
    if (this.road) {
      this.sideLights = new SideLightsComponent(10, () => AssetLoader.shared.getTexture('road:light_bezel'));
      this.boardRun = new BoardRunComponent({
        ringTexture: () => this.roadPiece('road_ring'),
        hubTexture: () => this.roadPiece('road_hub'),
        coinTexture: AssetLoader.shared.getTexture('shared:icon:coin'),
        art: (name) => AssetLoader.shared.getTexture(RING_ART[name as keyof typeof RING_ART] ?? `road:${name}`),
      });
      this.sideLights.setReady(this.pendingSideLights);
      const hudIndex = this.app.stage.getChildIndex(this.hud);
      this.app.stage.addChildAt(this.sideLights, hudIndex);
      this.app.stage.addChildAt(this.boardRun, hudIndex + 1);
      // The panel sits over the reels' surroundings, under the HUD; the run
      // bar just above the HUD, since it lies where the HUD pins the
      // free-spin chip (which would cover the run's status line). Every
      // pop-up (paytable, gamble, bonus) draws above both.
      const art = (name: string) => this.roadTexture(name);
      this.roadPanel = new RoadPanel(art);
      this.roadPanel.themeId = this.theme.id;
      // The road moves on the slot's collector symbol (a flower, a banner).
      const collector = this.theme.slot.symbols.find((s) => s.id === 'cherry')?.label;
      if (collector) this.roadPanel.collectorName = collector.toLowerCase();
      this.roadPanel.onChipTap = () => {
        this.roadExpanded = !this.roadExpanded;
        if (this.lastCtx) this.layoutRoadChrome(this.lastCtx);
      };
      this.roadPanel.visible = false;
      // A lit friend's lamp whose first game is still free offers it.
      this.roadPanel.onFriendPlay = (friend) => {
        void this.showFriendJoin(friend, true, friend.title.toUpperCase()).then((choice) => {
          if (choice === 'play') this.onFriendFreePlay?.(friend.id);
        });
      };
      // So does a Wheel Run lamp, under its game's name.
      this.roadPanel.onLampPlay = (lamp) => {
        const game = lampGameOf(this.theme.id, lamp);
        if (!game) return;
        void this.showLampLit(game, true, true).then((choice) => {
          if (choice === 'play') this.onLampFreePlay?.(game.lamp);
        });
      };
      this.roadPanel.onGiftTap = () => this.onRoadGiftTap?.();
      this.runBar = new RunBar(art);
      this.hiloDialog = new HiLoDialog(art, async (from, amount, fly) => {
        if (fly) await this.flyCoinsToWallet(from, amount);
        else if (amount > 0) this.model.credit(amount, 'collect');
      });
      this.app.stage.addChildAt(this.roadPanel, hudIndex + 2);
      this.app.stage.addChildAt(this.runBar, this.app.stage.getChildIndex(this.hud) + 1);
      // Side lights say what they're for when tapped.
      this.sideLights.eventMode = 'static';
      this.sideLights.cursor = 'help';
      this.sideLights.on('pointertap', (e) => this.explainLights(e.global));
      void this.roadArt?.then(() => {
        // The slot may have closed (or been built again) while the art loaded.
        if (!this.roadPanel || this.roadPanel.destroyed) return;
        this.roadPanel.refreshArt();
        this.runBar?.refreshArt();
        this.landmarkRoad?.refreshArt();
      });
      // Anything the host sent before the panel existed.
      this.setRoadState(this.roadNow.road, this.roadNow.spinKey);
      this.setRunInfo(this.runNow.view, this.runNow.status, this.runNow.notice);
      this.landmarkRoad = new LandmarkRoadComponent({
        texture: (alias) => AssetLoader.shared.getTexture(alias),
        floor: () => AssetLoader.shared.getTexture(this.theme.assets?.scene?.columnBackground?.alias ?? ''),
        coins: () => this.model.balance,
        wallet: () => this.hud?.balanceLabelPosition ?? null,
        canOpen: () => this.idle && this.hud?.currentSpinMode === 'spin',
        titleCard: (text, options) => this.playTitleCard(text, options),
      });
      this.landmarkRoad.setView(this.pendingLandmarkRoad);
      this.app.stage.addChildAt(this.landmarkRoad, hudIndex);
      const sideLights = this.sideLights;
      const boardRun = this.boardRun;
      const landmarkRoad = this.landmarkRoad;
      // While a landmark's restore card or moment covers the slot, the
      // overlays above the canvas step aside too (it has no open event, so
      // its state is read each frame: one boolean).
      let landmarkCover: (() => void) | null = null;
      this.app.ticker.add((ticker) => {
        sideLights.tick(ticker.deltaMS);
        boardRun.tick(ticker.deltaMS);
        landmarkRoad.tick(ticker.deltaMS);
        if (landmarkRoad.destroyed) return;
        if (landmarkRoad.blocking && !landmarkCover) landmarkCover = this.coverSlot();
        else if (!landmarkRoad.blocking && landmarkCover) {
          landmarkCover();
          landmarkCover = null;
        }
      });
    }

    // The hold-and-spin board takes the road panel's place during the
    // bonus, so it draws over the panel and run bar but under the HUD's
    // pop-ups; its end board sits over everything.
    if (this.holdAndSpinComponent) this.app.stage.addChild(this.holdAndSpinComponent);
    if (this.bonusWinDialog) this.app.stage.addChild(this.bonusWinDialog);
    // The landmarks' card and restore moment cover everything.
    if (this.landmarkRoad) this.app.stage.addChild(this.landmarkRoad.overlay);

    // The TV moment before a run or a game, over everything.
    this.titleCard = new TitleCard();
    this.app.stage.addChild(this.titleCard);

    // Level-ups, celebrated on the canvas.
    if (this.celebrateOnCanvas && this.profile) {
      const profile = this.profile;
      this.levelToast = new LevelUpToast();
      this.app.stage.addChild(this.levelToast);
      this.profileSubscriptions.push(
        profile.events.on('level-up', (event) => {
          if (!this.levelToast) return;
          this.app.stage.addChild(this.levelToast);
          void this.levelToast.show(event.level);
        }),
      );
    }

    const ctx: SlotLogicContext = {
      model: this.model,
      machine: this.machine,
      winLines: this.winLines,
      payoutTable: this.payoutTable,
      wildSymbolId: this.theme.slot.wildSymbolId,
      scatterSymbolId: this.theme.slot.scatterSymbolId ?? null,
    };
    this.runSpinLogic = new RunSpinLogic(ctx);
    this.collectLogic = new CollectLogic(ctx);
    this.holdAndSpinLogic = new HoldAndSpinLogic(this.machine, this.theme.slot.symbols);

    // Hold-tap is disabled at first (no spin yet to hold from). Enabled
    // after the first spin completes via exitToIdle().
    this.machine.setHoldInteractive(false);

    // Audio cues — the binding owns its subscriptions on the model and
    // tears them down in disposeResources.
    this.audioBinding = new SlotAudioBinding(this.model);

    // The reels are built from the first-spin essentials; now fetch the
    // rest of the theme and its audio in the background (see preload).
    void AssetLoader.shared.loadTheme(this.theme);
    void AudioBus.shared.loadTheme(this.theme);
    if (this.road) void AudioBus.shared.loadEntries(this.theme.assets?.road?.audio);

    // Reel collectibles, if the host turned them on before the build.
    if (this.pendingRelics) this.setRelics(this.pendingRelics);

    this.contentBuilt = true;
    if (this.storyWaiting) {
      const layer = this.storyWaiting;
      this.storyWaiting = null;
      this.mountStory(layer);
    }
    this.markBuilt();
  }

  protected onResize(): void {
    this.layout();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    // Any keypress doubles as the user-gesture that lets the AudioContext
    // come out of its suspended state — the spin's own SFX will then play.
    void AudioBus.shared.resume();
    // A mini-game on screen takes the keys: no spinning behind it.
    if (this.miniGames?.busy) return;

    if (event.code === 'Space') {
      event.preventDefault();
      // A board dialog over the slot (a friend saying hello, a game) takes
      // Space for itself: it never spins or STOPs behind it.
      if (this.app.stage.children.some((c) => c instanceof BoardDialog && c.visible)) return;
      void this.handleSpin();
    } else if (event.code === 'KeyI') {
      this.cycleInfoModal();
    }
  }

  protected disposeResources(wasInitialized: boolean): void {
    // No stopMusic / stopAmbient here on purpose. The next view's mount
    // (WorldMapV2's lobby music, or a sibling SlotDemoScene's biome
    // music) calls playMusic with its own alias and AudioBus cross-
    // fades automatically. Stopping here would briefly cut the audio
    // before the next view's playMusic fired.
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    this.profileBalanceSubscription?.dispose();
    this.profileBalanceSubscription = null;
    this.gamblePrefSubscription?.dispose();
    this.gamblePrefSubscription = null;
    this.audioBinding?.dispose();
    this.audioBinding = null;
    this.dropRoadCoins();
    this.model.dispose();
    if (!wasInitialized) return;

    this.hud?.destroy();
    this.hud = null;
    this.gambleFlow?.destroy();
    this.gambleFlow = null;
    this.winSequence?.destroy();
    this.winSequence = null;
    this.paytable?.destroyPaytable();
    this.paytable = null;
    this.bonusWinDialog?.destroy({ children: true });
    this.bonusWinDialog = null;
    for (const sub of this.profileSubscriptions) sub.dispose();
    this.profileSubscriptions = [];
    for (const board of [this.settingsBoard, this.walletBoard, this.findReveal, this.levelToast, this.dailyBoard]) board?.destroy({ children: true });
    this.dailyBoard = null;
    this.settingsBoard = null;
    this.walletBoard = null;
    this.findReveal = null;
    this.levelToast = null;
    this.spinHintSlot?.release();
    this.spinHintSlot = null;
    this.spinHint?.destroy({ children: true });
    this.spinHint = null;
    // Leaving mid-bonus or mid-card: let the host bring its overlays back.
    if (this.covers > 0) this.onCanvasPopupChange?.(false);
    this.covers = 0;
    this.bonusShown = false;
    this.bonusCover = null;
    this.holdAndSpinComponent?.destroy();
    this.holdAndSpinComponent = null;
    this.holdAndSpinLogic = null;
    this.trailChrome?.destroy();
    this.trailChrome = null;
    this.nameFlash?.destroy({ children: true });
    this.nameFlash = null;
    this.sideLights?.destroy({ children: true });
    this.sideLights = null;
    this.boardRun?.destroy({ children: true });
    this.boardRun = null;
    this.landmarkRoad?.overlay.destroy({ children: true });
    this.landmarkRoad?.destroy({ children: true });
    this.landmarkRoad = null;
    this.relics?.destroy({ children: true });
    this.relics = null;
    this.ambientLife?.destroy({ children: true });
    this.ambientLife = null;
    if (this.takeWin) {
      this.takeWin.destroyEffect();
      this.takeWin = null;
    }
    this.gemGlow?.destroy({ children: true });
    this.gemGlow = null;
    if (this.machine) {
      this.machine.destroyMachine();
      this.machine = null;
    }
  }

  private layout(): void {
    if (
      !this.machine ||
      !this.hud ||
      !this.winSequence ||
      !this.paytable
    ) {
      return;
    }
    // gambleFlow is optional — null in multiplayer Reel Race. Skip
    // its layout pass when absent rather than bailing on the whole
    // scene layout.
    const { width, height } = this.app.renderer;
    const naturalWidth = this.machine.totalWidth();
    const naturalHeight = this.machine.totalHeight();
    // Scale to fit the viewport, but generously — symbols at native
    // 72px get hard to read on anything bigger than a phone. Targets:
    //   • mobile (≤640px wide):  fill to gutters (was the only mode)
    //   • tablet/desktop:        target ~62% of viewport width OR
    //                            whatever fits the available height
    //                            after reserving room for the HUD,
    //                            balance, spin button and theme switch
    //                            below the machine
    //   • cap at 2.5× so we don't pixelate on 4K monitors
    // Earlier {@code Math.min(1, ...)} cap meant the slot stayed
    // 424px wide on a 1500px desktop — symbols read tiny. UX feedback
    // from playtest: "actual symbols are too small/hard to see."
    const horizontalGutter = 24;
    // Reserve ~280px below the machine for HUD + spin button + theme
    // switch + balance label so the machine doesn't crowd them.
    const verticalReserve = 280;
    // Phone detection. Pixi's renderer.width is ALREADY CSS px (the DPR
    // lives in the backing buffer via `resolution`; renderer.width tracks
    // window.innerWidth), so compare it to 640 directly. An earlier version
    // divided by resolution on the false premise that renderer.width was
    // device px; that mis-tagged Retina (DPR 2) desktops narrower than
    // 1280 CSS as "mobile", so the slot took the full-width phone path and
    // overran the Reel Race seat rail (producer 2026-06-10). Phones
    // (<640 CSS) fill the width with a thinner frame.
    const isMobile = width < 640;
    // Border-ring thickness as a fraction of the reel width — thinned on
    // phones (0.022 vs 0.045) so the painted frame eats ~30% less and the
    // reels grow into the reclaimed width.
    const ringFraction = isMobile ? 0.022 : 0.045;
    // Budget for the ring so the OUTER frame (reels + 2×ring) fills the
    // gutter rather than the ring overrunning it once the reels fill width.
    // Phones: near edge-to-edge, with a hair of margin so the frame doesn't
    // kiss the screen edges (true full-width read "ever so slightly too
    // wide", 2026-06-10). ~3 CSS px each side, DPR-scaled so the margin is
    // consistent across devices. Tablet/desktop keep the 24px gutter.
    const sideGutter = isMobile
      ? 3 * (this.app.renderer.resolution || 1)
      : horizontalGutter;
    const maxByWidth =
      (width - sideGutter * 2) / (naturalWidth * (1 + ringFraction * 2));
    const maxByHeight = this.chromeInsets
      ? placeBetweenInsets({
          height,
          naturalHeight,
          top: this.chromeInsets.top,
          bottom: this.chromeInsets.bottom,
          ring: isMobile ? 14 : 24,
          scale: 1,
        }).maxScale
      : Math.max(0.5, (height - verticalReserve) / naturalHeight);
    const fitScale = Math.min(maxByWidth, maxByHeight);
    // Target ~62% of viewport width on desktop; floor at 1× (native).
    const desktopTarget = (width * 0.62) / naturalWidth;
    const desktopScale = Math.min(2.5, Math.min(fitScale, desktopTarget));
    // Phones fill the width (no 62% desktop cap) so the reels are as large
    // as the gutter + thinned ring allow; tablet/desktop keep the 62% feel.
    const scale = isMobile
      ? Math.min(2.5, fitScale)
      : Math.max(1, desktopScale);
    this.machine.scale.set(scale);
    const scaledWidth = naturalWidth * scale;
    const scaledHeight = naturalHeight * scale;
    // Centre the visible slot — the slot machine's container origin
    // sits at the reel-grid top-left, and {@link totalWidth} +
    // {@link totalHeight} return the reel-grid+gutter dimensions.
    // We add the framePad offset back so the OUTER frame (when
    // present) is centred rather than the reel-grid origin.
    const padDisplay = this.machine.topPadding() * scale;
    // When a border overlay is active the slot's framePad is zero,
    // so the visible slot's outer extent equals the reel grid. We
    // factor the border's display ring thickness into centering so
    // the slot+border combined visual is centred on screen.
    //
    // Ring thickness scales with the slot's rendered width so the
    // wooden chrome reads at roughly the same proportion on phones
    // and desktops. A fixed pixel value (the old approach) made the
    // frame feel chunky on mobile — a ~4.5% target gives parity with
    // the desktop ratio that playtest already approved. The constant
    // {@link BORDER_DISPLAY_RING_PX} is kept as the cap (so the ring
    // never overruns the original max) and as the floor on tiny
    // viewports.
    const ringDisplay = this.borderSprite
      ? Math.max(
          isMobile ? 14 : 24,
          Math.min(
            isMobile ? 30 : BORDER_DISPLAY_RING_PX,
            Math.round(scaledWidth * ringFraction),
          ),
        )
      : 0;
    const machineX = Math.round((width - scaledWidth) / 2 + padDisplay);
    const machineY = this.chromeInsets
      ? placeBetweenInsets({
          height,
          naturalHeight,
          top: this.chromeInsets.top,
          bottom: this.chromeInsets.bottom,
          ring: ringDisplay,
          scale,
        }).machineY + padDisplay
      : Math.round((height - scaledHeight) / 2 - 56 + 16 + padDisplay);
    this.machine.x = machineX;
    this.machine.y = machineY;

    // Border overlay — a slim decorative ring drawn around the slot's
    // OUTER frame (i.e., the painted box that includes the FRAME_PAD
    // gutter, NOT the reel grid origin). The slot machine's container
    // origin is at the reel grid's top-left, with the outer frame
    // painted at (-FRAME_PAD, -FRAME_PAD) in container-local coords;
    // we offset the border sprite by the same amount so its outer
    // edge aligns with the slot's painted exterior.
    //
    // {@link NineSliceSprite} keeps the ring's display thickness
    // SYMMETRIC (top, bottom, left, right all the same number of
    // pixels) regardless of slot aspect ratio. The ring's full
    // painted detail (brass caps + carved oak ALL the way to the
    // transparent magenta-mask centre) lives in the corners + edges
    // 9-slice regions; the empty middle stretches to fill. We apply
    // a uniform sprite scale so the corners + edges render at a slim
    // size on display while preserving the full source detail —
    // sprite.width is pre-scale-divided so the final display width
    // still equals the slot's outer width.
    if (this.borderSprite) {
      const cornerInset = this.borderSprite.leftWidth;
      // Display ring thickness is the fixed BORDER_DISPLAY_RING_PX
      // — slim and uniform regardless of slot scale. The painted
      // ring sits OUTSIDE the slot's reel-grid (framePad=0 when
      // border is active), so its inner edge butts against the
      // reel grid's outer edge with no visible gap. Sprite scale
      // maps the texture's full painted-ring inset down to the
      // target display thickness; sprite.width / .height are
      // pre-divided so the final display dimensions still wrap
      // the reel grid + ring on every side.
      const cornerScale = ringDisplay / cornerInset;
      const outerW = scaledWidth + ringDisplay * 2;
      const outerH = scaledHeight + ringDisplay * 2;
      this.borderSprite.scale.set(cornerScale);
      this.borderSprite.width = outerW / cornerScale;
      this.borderSprite.height = outerH / cornerScale;
      this.borderSprite.x = machineX - ringDisplay;
      this.borderSprite.y = machineY - ringDisplay;
    }

    // Expand the layout context's slot bounds out to include the
    // border ring on each side. HUD elements (BALANCE label above,
    // SPIN button + BET selector below) anchor to these bounds, so
    // when a border is active they sit just outside the wooden frame
    // rather than overlapping the painted top/bottom friezes.
    // Outer-frame extents — machineX is the reel-grid origin shifted
    // right by padDisplay, so the painted frame's left edge sits at
    // (machineX - padDisplay - ringDisplay). When a border ring is
    // active padDisplay is zero; without one ringDisplay is zero. The
    // resulting bounds hug the visible painted box exactly, which is
    // what HUD elements like the BET pill / INFO disc anchor to.
    const machineLeft = machineX - padDisplay - ringDisplay;
    const machineRight = machineLeft + scaledWidth + ringDisplay * 2;
    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineLeft: Math.round(machineLeft),
      machineRight: Math.round(machineRight),
      machineTop: machineY - ringDisplay,
      machineBottom: machineY + scaledHeight + ringDisplay,
      spinButtonY: Math.round(machineY + scaledHeight + ringDisplay + 36),
      frameRing: ringDisplay,
    };
    this.hud.layout(ctx);
    this.relics?.layout(ctx);
    this.ambientLife?.layout(ctx);
    this.placeSpinHint(ctx);
    this.sideLights?.layout(ctx);
    this.boardRun?.layout(ctx);
    this.landmarkRoad?.layout(ctx);
    this.lastLayout = ctx;
    this.storyLayer?.layout(ctx);
    this.gambleFlow?.layout(ctx);
    this.winSequence.layout(ctx);
    // The slot's boards keep clear of the wallet bar drawn over the canvas.
    const top = this.boardTop();
    for (const board of [this.bonusWinDialog, this.paytable, this.itemsCatalog, this.settingsBoard, this.walletBoard, this.findReveal, this.dailyBoard]) {
      board?.setTopInset(top);
      board?.layout(width, height);
    }
    // A level reached shows over the top of the reels, where the eye is.
    this.levelToast?.layout(width, Math.round(ctx.machineTop + (ctx.machineBottom - ctx.machineTop) * 0.22));
    this.holdAndSpinComponent?.layout(ctx);
    this.titleCard?.layout(width, height);
    this.hiloDialog?.layout(width, height);
    this.lastCtx = ctx;
    this.layoutRoadChrome(ctx);
    this.trailChrome?.layout(ctx);
    this.placeNameFlash(ctx);
    this.friendJoin?.layout(width, height);
    this.onLayoutBounds?.({
      rendererWidth: width,
      rendererHeight: height,
      machineLeft: ctx.machineLeft ?? 0,
      machineRight: ctx.machineRight ?? width,
      machineTop: ctx.machineTop ?? 0,
      machineBottom: ctx.machineBottom ?? height,
      spinButtonY: ctx.spinButtonY,
    });
  }

  /**
   * Build the 9-slice border sprite for the slot machine. Source is a
   * square texture (~1254px) with a slim painted ring (80px on a
   * 1024-equivalent source, per asset brief pack 2 §20 revised) and a
   * transparent magenta-masked centre.
   *
   * <p>The corner inset gives display ring thickness (NineSliceSprite
   * renders corners at native pixel size). 6% of the source dimension
   * (~60–75px on a 1024–1254 source) reads as a slim wood-plank rim
   * that doesn't dominate the playfield. The middle 9-slice region
   * is fully transparent (the magenta-mask post-process zeroes alpha
   * past depth ~98px from each edge), so any painted band that
   * overflows the inset boundary stretches into invisible territory.
   *
   * <p>Returns null when the active theme doesn't declare a border —
   * the slot scene falls back to the rounded-rect SlotMachineFrame
   * stroke, which works fine without any border art.
   */
  private buildBorderSprite(): NineSliceSprite | null {
    const borderEntry = this.theme.assets?.scene?.border;
    if (!borderEntry) return null;
    const texture = AssetLoader.shared.getTexture(borderEntry.alias);
    if (!texture) return null;
    // Inset matches the source's painted-ring depth EXACTLY so the
    // 9-slice corners contain the full painted decoration with no
    // transparent buffer on the inner side. Any buffer would show
    // up as a visible gap between the painted ring's inner edge and
    // the reel grid's outer edge at display.
    //
    // Source painted-ring depths (post-trim, asset pipeline §20.6 + a
    // uniform-depth trim that crops the inner anti-alias band so all
    // four sides match the smaller of top vs side):
    //   • Forest (5:3 aspect, 2005×1254): ring uniformly 152px on
    //     every side after the §20 trim. 152/1254 ≈ 12.1%.
    //   • Other commissioned themes (1254×1254 square): ring ~131px
    //     uniform — slightly under the 152 target, leaves a ~21
    //     source-px transparent buffer that displays as ~7px gap.
    //     Acceptable until the other themes regenerate to match
    //     forest's choreography; trimming further now would chop too
    //     much detail off the existing art.
    //
    // The 152/1254 ratio means inset scales with the source's smaller
    // dimension, so a future theme shipped at a different resolution
    // gets a proportional inset automatically.
    const sourceMinDim = Math.min(texture.width, texture.height);
    const inset = Math.round(sourceMinDim * (152 / 1254));
    return new NineSliceSprite({
      texture,
      leftWidth: inset,
      topHeight: inset,
      rightWidth: inset,
      bottomHeight: inset,
    });
  }

  private async handleSpin(): Promise<void> {
    // A landmark's restore card or moment is up: SPIN waits.
    if (this.landmarkRoad?.blocking) return;
    // During a Lamp Run, SPIN (and Space) is the run's button.
    if (this.hud && this.hud.currentSpinMode !== 'spin') {
      this.handleBoardButton(this.hud.currentSpinMode);
      return;
    }
    if (
      !this.machine ||
      !this.hud ||
      !this.winSequence ||
      !this.runSpinLogic ||
      this.spinning
    ) {
      return;
    }
    this.refreshSpinMode();
    this.followProfileBalance();
    // CRITICAL — flip the re-entry guard BEFORE any await. Producer
    // bug 2026-06-04: with auto-collect added, the original spinning=
    // true assignment sat 15 lines below after `await collectPending()`,
    // so a second tap on SPIN during the coin-fly animation passed the
    // {@code this.spinning} guard above (still false) and entered
    // handleSpin a second time — running the collect twice, queueing
    // two spins, the works. Setting it true the moment the guard
    // passes blocks re-entry for the full lifetime of one tap.
    // hud.setSpinning(true) also hides the SPIN medallion visually
    // during the collect so the player can't keep mashing it.
    this.spinning = true;
    this.hud.setSpinning(true);

    // 2026-06-04 — SPIN auto-collects any pending winnings before
    // starting the next spin. Old UX showed a modal GAMBLE/COLLECT
    // overlay forcing the player to pick; new UX lets them tap SPIN
    // (= collect + spin) or the inline GAMBLE medallion (= open
    // overlay).
    //
    // PARALLEL collect — Jon's brief 2026-06-04: "is there a reason
    // we have to wait for the collect coins animation to complete
    // before the reels can spin?" — no, there isn't. The model state
    // (balance += winnings, pendingWinnings = 0) updates synchronously
    // at the start of {@code collectLogic.execute}; the coin-fly
    // animation is purely visual and runs against a side-effect that
    // just bumps the HUD balance display. Fire {@code runCollect}
    // without awaiting and the reels start spinning immediately while
    // the coins still fly into the balance pill. (We can't call
    // {@code gambleFlow.collectPending} async-without-await here —
    // that path also fires {@code callbacks.onExit} → {@link exitToIdle}
    // at the end, which would race with the spin's state. Calling
    // {@code runCollect} directly skips the onExit step since the
    // spin's own {@link exitToIdle} fires at the end.)
    if (this.gambleFlow && this.model.pendingWinnings > 0) {
      this.hud.setGambleButtonVisible(false);
      void this.runCollect();
    }
    // gambleFlow is optional — when null (multiplayer Reel Race) the
    // post-win path collects immediately instead of offering gamble.

    const spinBet = this.model.currentBet;
    this.hud.setResultText(`bet ${spinBet} spinning…`);
    this.machine.setHoldInteractive(false);
    this.gemGlow?.clear();

    // Side-channel hook: fire once per spin commit so external systems
    // (e.g. the multiplayer Reel Race) can react. Errors swallowed so
    // a flaky upstream doesn't break the spin animation.
    if (this.onSpinFired) {
      try { this.onSpinFired(spinBet); }
      catch (err) {
        console.warn('[SlotDemoScene] onSpinFired threw; ignoring', err);
      }
    }

    const result = this.serverSpins
      ? await this.runServerSpin(spinBet)
      : await this.runSpinLogic.execute();
    if (!result.ran || !result.settlement || !result.outcome) {
      // Spin didn't actually run (debit failed locally OR server
      // rejected). HUD's not-enough-coins listener has already shown
      // the message in local mode; for server failures we surface a
      // fallback. Either way, restore controls.
      this.exitToIdle();
      return;
    }

    const { outcome, settlement } = result;
    const inFreeSpinRun = result.wasFreeSpin === true || this.model.freeSpinsRemaining > 0;

    // Gem cells (producer brief 2026-06-04; back on the server's reels with
    // reel_gems, 2026-10-21) pay gems, not coins, wherever they land. The
    // gems the server paid count from its figure (never the cells' own
    // values), the rest from the cells, and the moment plays for them all.
    const gemMoment = gemCellsMoment(outcome, settlement, result.reelGems);
    if (gemMoment) this.playGemCells(gemMoment);

    // Trigger detection: server-mode reads `pendingServerBonus`
    // (set by runServerSpin from the response); local-mode counts
    // money cells on the freshly generated outcome.
    const triggeredHoldAndSpin = this.serverSpins
      ? this.pendingServerBonus !== null
      : detectMoneyCells(outcome).length >= DEFAULT_HOLD_AND_SPIN_TRIGGER;
    if (triggeredHoldAndSpin) {
      AudioBus.shared.play('slot:bonus_drop');
    }

    if (settlement.totalWinnings > 0) {
      // Capture every cell that contributed to the win — used by
      // {@link runCollect} as the source array for the take-win
      // flying-coin animation so coins spawn from each winning symbol
      // rather than a single burst centre. Combines payline cells (the
      // first {@code matchCount} positions of each winning line) with
      // scatter cells (which can sit anywhere on the grid).
      this.lastWinningCellPositions = this.collectWinningCellPositions(settlement);

      // Pass the current bet so WinSequenceComponent can classify the
      // win tier and swap in the BIG WIN / MEGA WIN / JACKPOT type-
      // treatment art when the win clears the threshold.
      this.winSequence.playWin(settlement, spinBet);
      this.hud.setResultText(describeWin(settlement, result.freeSpinsGranted ?? 0, result.wasFreeSpin === true));
      // Await the highlight when scatters triggered new free spins or
      // money symbols hit the hold-and-spin threshold — we want the
      // player to see the cells pulse before the modal celebration
      // interrupts. Otherwise fire-and-forget.
      const triggeredFreeSpins = (result.freeSpinsGranted ?? 0) > 0;
      const highlightDone = this.machine.highlightWins(
        settlement.settlements,
        this.winLines,
        settlement.scatter?.positions ?? [],
      );
      if (triggeredFreeSpins || triggeredHoldAndSpin) {
        await highlightDone;
        if (triggeredFreeSpins) {
          const granted = result.freeSpinsGranted ?? 0;
          await this.playTitleCard('FREE SPINS!', {
            sub: `${granted} free spin${granted === 1 ? '' : 's'} to play`,
            sting: BONUS_STING,
            kind: 'win',
          });
        }
      } else {
        void highlightDone;
      }
    }

    // Multiplayer-Reel-Race bonus cells live outside the payline /
    // scatter flow — they're token-collect cells the server dropped
    // into the grid. Pulse them with the lighter scale-flash so a
    // single bonus reads as "collected a token", distinct from the
    // per-symbol win-celebration video reserved for 3+ payline wins.
    // The meter-side {@code +N} burst (WheelReelRaceView) lands
    // alongside as the canonical "added to your tokens" signal.
    // No-op on local-mode spins (the bonus symbol weights to 0 in
    // forestTheme.ts so the local RNG never picks it).
    const bonusPositions = detectBonusCells(outcome);
    if (bonusPositions.length > 0) {
      this.machine.playBonusCollect(bonusPositions);
    }
    // Item-drop cells: if the host queued a full cycle-and-settle
    // reveal via {@link playItemDropReveal}, consume it now. The
    // queued reveal carries its own pulse + cycle + settle, so we
    // do NOT include item-drop positions in {@code playBonusCollect}
    // above (would otherwise double-pulse the cell). If no reveal is
    // queued (defensive: e.g. host didn't wire it, or the spin was
    // local-mode and the symbol leaked from a fixture), fall back to
    // the bonus-style pulse so the star at least flashes.
    const itemDropPositions = detectItemDropCells(outcome);
    if (itemDropPositions.length > 0) {
      const pending = this.pendingItemDropReveal;
      if (pending) {
        this.pendingItemDropReveal = null;
        const col = pending.cell % 5;
        const row = Math.floor(pending.cell / 5);
        // Fire-and-forget — the queued promise resolves when
        // playItemDropReveal's pulse/cycle/settle chain completes,
        // which is what the host awaits to schedule its chip-fly.
        void this.machine
          .playItemDropReveal({ col, row }, pending.cycleDefs, pending.finalDef)
          .then(pending.resolve);
      } else {
        this.machine.playBonusCollect(itemDropPositions);
      }
    }

    // Reel collectibles: the relic flies to the shelf, then its find board
    // or, for the third, the set's moment, before any bonus or collect.
    const collect = this.pendingCollect;
    this.pendingCollect = null;
    if (collect && this.relics) await this.playRelics(collect, settlement.totalWinnings > 0);

    // Hold-and-spin runs after any payline win-highlight + free-spin
    // fanfare so the player sees one celebration at a time. Server
    // mode drives the loop via /spin requests; local mode runs the
    // engine's HoldAndSpinLogic. Either way the post-bonus pipeline
    // (runCollect for the base settlement) is identical.
    let bonusWinnings = 0;
    if (triggeredHoldAndSpin) {
      if (this.serverSpins && this.pendingServerBonus !== null) {
        const initial = this.pendingServerBonus;
        this.pendingServerBonus = null;
        bonusWinnings = await this.runServerBonusFlow(initial, settlement.totalWinnings);
      } else {
        bonusWinnings = await this.runHoldAndSpinBonus(outcome, spinBet);
      }
    }

    if (settlement.totalWinnings > 0) {
      if (inFreeSpinRun || triggeredHoldAndSpin) {
        // Free-spin path OR hold-and-spin path: skip the GAMBLE / COLLECT
        // offer and credit straight to the wallet. Industry convention is
        // no gamble mid-bonus or post-bonus; cs-core's MMS works the same
        // way. Bonus winnings already credited inside runHoldAndSpinBonus.
        this.model.setPendingWinnings(settlement.totalWinnings);
        void this.runCollect().then(() => this.exitToIdle());
      } else if (!this.gambleOffered()) {
        // No gamble offer (the Reel Race, or the player switched gamble
        // off in Settings): collect at once so the next spin is one tap.
        this.model.setPendingWinnings(settlement.totalWinnings);
        void this.runCollect().then(() => this.exitToIdle());
      } else {
        // 2026-06-04 — new inline-GAMBLE UX. Pending winnings stage on
        // the model so SPIN can auto-collect on the next tap; the
        // inline HUD GAMBLE medallion lights up so the player can
        // open the card-pick overlay if they want to double. The old
        // floating GAMBLE/COLLECT modal buttons no longer show.
        //
        // Clear the in-flight spin state so SPIN re-appears next to
        // GAMBLE — without these lines {@link SlotHudComponent#setSpinning}
        // (= true since handleSpin start) keeps the spin medallion
        // hidden, leaving GAMBLE sitting alone at the right with an
        // empty cluster centre.
        //
        // setHoldInteractive(true) — bug fix 2026-06-04: handleSpin
        // line 866 disables hold for the duration of the spin. Old
        // win-flow eventually called exitToIdle (after GAMBLE/COLLECT
        // resolved) which re-enabled hold. The new inline UX skips
        // exitToIdle until the player either gambles+loses or auto-
        // collects, so without this line the player can't tap reels
        // to hold between spins after any winning spin.
        this.spinning = false;
        this.hud.setSpinning(false);
        this.machine.setHoldInteractive(true);
        this.model.resetGambleStreak();
        this.model.setPendingWinnings(settlement.totalWinnings);
        this.hud.setGambleButtonVisible(true);
      }
    } else if (triggeredHoldAndSpin) {
      // No payline win, but the bonus paid out — show its result + idle.
      this.hud.setResultText(
        `🎰 HOLD & SPIN · +${bonusWinnings.toLocaleString()}`,
      );
      this.exitToIdle();
    } else {
      const middleSummary = outcome.grid.map((reel) => reel[1]?.glyph ?? '?').join(' · ');
      const freeSuffix = inFreeSpinRun ? '   (free spin)' : '';
      this.hud.setResultText(`${middleSummary}   no win${freeSuffix}`);
      this.exitToIdle();
    }
  }

  /**
   * Run one full hold-and-spin bonus locally (no server): its title card,
   * then {@link HoldAndSpinLogic} drives the respins on the machine while
   * the {@link HoldAndSpinComponent} shows the coins and the board, then
   * the end board pays the coins into the wallet.
   *
   * <p>Returns the bonus's coin payout, for the result line.
   */
  private async runHoldAndSpinBonus(
    triggerOutcome: SpinOutcome,
    bet: number,
  ): Promise<number> {
    if (!this.holdAndSpinLogic || !this.holdAndSpinComponent || !this.machine) {
      return 0;
    }
    await this.playHoldAndSpinIntro(DEFAULT_HOLD_AND_SPIN_RESPINS);
    // Fresh per-run model so subscribers see every event including start.
    const bonusModel = new HoldAndSpinModel({
      gridCols: this.machine.config.reelCount,
      gridRows: this.machine.config.visibleRows,
      initialRespins: DEFAULT_HOLD_AND_SPIN_RESPINS,
    });
    this.beginHoldAndSpin(bonusModel);
    const result = await this.holdAndSpinLogic.execute({
      model: bonusModel,
      triggerOutcome,
      bet,
    });
    await this.payHoldAndSpin(bonusModel, result.totalWinnings, result.fullGrid);
    return result.totalWinnings;
  }

  /**
   * Phase D.3 — server-authoritative hold-and-spin loop. Server is the
   * authority: it picks money values, decides which respin lands what,
   * detects completion, and credits the wallet on the final response.
   * Client renders only — drives a local {@link HoldAndSpinModel} from
   * server response state diffs so the coins and the board react via the
   * {@link HoldAndSpinComponent} subscriptions.
   *
   * <p>{@code initialState} is the trigger spin's
   * {@code holdAndSpin.phase === 'started'} payload. {@code baseSettlementWinnings}
   * is the trigger spin's payline + scatter total — passed in so the
   * final {@code setBalance} can compute the post-debit pre-base-credit
   * value the caller's {@code runCollect} expects (D.2 fix logic
   * extended for the bonus case).
   *
   * <p>Returns the total bonus winnings — used for the post-bonus
   * result text in {@link handleSpin}.
   */
  private async runServerBonusFlow(
    initialState: HoldAndSpinResponseState,
    baseSettlementWinnings: number,
  ): Promise<number> {
    if (!this.holdAndSpinComponent || !this.machine || !this.spinFn) {
      return 0;
    }
    await this.playHoldAndSpinIntro(initialState.respinsRemaining);

    // Fresh per-run model. The component subscribes to its events to
    // pop the coins in and keep the board's lamps and total up to date.
    const bonusModel = new HoldAndSpinModel({
      gridCols: this.machine.config.reelCount,
      gridRows: this.machine.config.visibleRows,
      initialRespins: initialState.respinsRemaining,
    });
    this.beginHoldAndSpin(bonusModel);

    // Each coin's size of prize picks its art (a coin, a stack, a bag or a
    // chest), as the local bonus does.
    const bet = initialState.bet;
    const toCell = (c: { col: number; row: number; value: number }) => ({
      col: c.col,
      row: c.row,
      value: c.value,
      betMultiplier: bet > 0 ? c.value / bet : undefined,
    });
    bonusModel.start(initialState.lockedCells.map(toCell), bet);

    let finalResponse: SpinSlotResponse | null = null;
    // Safety cap — server should always reach a terminal state, but
    // 50 respins is well past the longest realistic bonus.
    for (let i = 0; i < 50; i++) {
      const response = await this.spinFn(bet);
      if (!response.holdAndSpin) {
        console.warn('[slot] respin response missing holdAndSpin state');
        break;
      }

      // Animate the reel land with the server's respin outcome.
      await this.machine.spinDriven(
        Promise.resolve(this.toEngineOutcome(response.outcome.grid)),
      );

      // Diff: which cells are new in this response vs what the model
      // already knows? The server's lockedCells is the post-respin
      // truth — anything we don't have locally landed this respin.
      const newCells = response.holdAndSpin.lockedCells
        .filter((c) => !bonusModel.hasCellLocked(c.col, c.row))
        .map(toCell);

      if (newCells.length > 0) {
        bonusModel.recordLandedCells(newCells);
      } else {
        bonusModel.consumeRespin();
      }

      if (response.holdAndSpin.phase === 'completed') {
        finalResponse = response;
        break;
      }
    }

    const totalBonusWinnings = finalResponse?.holdAndSpin?.totalBonusWinnings ?? 0;
    const fullGrid = finalResponse?.holdAndSpin?.fullGrid ?? false;

    // The server has paid already. Show the wallet without the bonus and
    // without the trigger spin's own win: the bonus's coins fly in next,
    // and the caller's runCollect adds the trigger win after (D.2 logic).
    if (finalResponse) {
      this.dropRoadCoins();
      this.model.setBalance(
        finalResponse.wallet.coins - baseSettlementWinnings - totalBonusWinnings,
        'spin-bet',
      );
      this.model.setFreeSpinsRemaining(finalResponse.freeSpinsRemaining);
    }

    bonusModel.complete();
    await this.payHoldAndSpin(bonusModel, totalBonusWinnings, fullGrid);
    return totalBonusWinnings;
  }

  /**
   * Show one of the slot's own pop-ups over everything, with the host's
   * overlays stepping aside, until `play` resolves (it closes).
   */
  private async showBoard<T extends BoardDialog>(board: T, play: (board: T) => Promise<void>): Promise<void> {
    const release = this.coverSlot();
    this.app.stage.addChild(board);
    board.setTopInset(this.boardTop());
    if (this.lastCtx) board.layout(this.lastCtx.width, this.lastCtx.height);
    try {
      await play(board);
    } finally {
      release();
    }
  }

  /** Where the wallet bar over the canvas ends: boards keep below it. */
  private boardTop(): number {
    return this.roadChrome?.top ?? 64 + safeAreaInsetTop();
  }

  private boardFloor = (): Texture | null => {
    const alias = this.theme.assets?.scene?.columnBackground?.alias;
    return alias ? AssetLoader.shared.getTexture(alias) : null;
  };

  /** The settings gear: the slot's own settings board, or the host's. */
  async openSettings(): Promise<void> {
    if (!this.settingsHost || !this.profile) {
      this.onOpenSettings?.();
      return;
    }
    if (this.settingsBoard?.visible) return;
    this.settingsBoard ??= new SettingsBoard(this.profile, this.settingsHost, this.boardFloor);
    await this.showBoard(this.settingsBoard, (b) => b.play());
  }

  /** What gems and coins are for (the host calls this when the wallet's
   *  gems are tapped over the slot). `reelGems`: gems land on this
   *  player's reels (reel_gems), which the board then says. */
  async showWallet(gems: number, coins: number, reelGems = false): Promise<void> {
    if (this.walletBoard?.visible) return;
    this.walletBoard ??= new WalletBoard({
      floor: this.boardFloor,
      coin: () => AssetLoader.shared.getTexture('shared:icon:coin'),
      gem: () => AssetLoader.shared.getTexture('shared:icon:gem'),
    });
    await this.showBoard(this.walletBoard, (b) => b.play(gems, coins, reelGems));
  }

  /**
   * The daily bonus over the slot, on a board (the host offers it when the
   * player has one waiting). Resolves when it has closed.
   */
  async playDailyBonus(offer: Omit<DailyBonusOffer, 'fly'>): Promise<void> {
    if (this.dailyBoard?.visible) return;
    const loader = AssetLoader.shared;
    this.dailyBoard ??= new DailyBonusBoard({
      floor: this.boardFloor,
      bezel: () => loader.getTexture('road:light_bezel'),
      coin: () => loader.getTexture('shared:icon:coin'),
      gift: () => loader.getTexture('shared:icon:gift'),
      check: () => loader.getTexture('shared:icon:check'),
    });
    await this.showBoard(this.dailyBoard, (b) =>
      b.play({ ...offer, fly: (from, amount) => this.flyCoinsToWallet(from, amount, false) }),
    );
  }

  /** Press the daily bonus board's button by its label (browser tests). */
  pressDailyBonus(label: string): boolean {
    return this.dailyBoard?.press(label) ?? false;
  }

  /** A relic found on the reels, shown on the find board. */
  private findQueue: Promise<void> = Promise.resolve();
  private showFind(find: Find): Promise<void> {
    this.findQueue = this.findQueue.then(async () => {
      if (!this.app?.stage) return;
      this.findReveal ??= new FindReveal(this.boardFloor);
      await this.showBoard(this.findReveal, (b) => b.play(find));
    });
    return this.findQueue;
  }

  /** The slot's own pop-ups as plain data (browser tests read this). */
  get popupUi(): {
    paytable: PaytableDisplay['snapshot'] | null;
    settings: SettingsBoard['snapshot'] | null;
    wallet: WalletBoard['snapshot'] | null;
    find: FindReveal['snapshot'] | null;
    level: LevelUpToast['snapshot'] | null;
    daily: DailyBonusBoard['snapshot'] | null;
  } {
    return {
      paytable: this.paytable?.snapshot ?? null,
      settings: this.settingsBoard?.snapshot ?? null,
      wallet: this.walletBoard?.snapshot ?? null,
      find: this.findReveal?.snapshot ?? null,
      level: this.levelToast?.snapshot ?? null,
      daily: this.dailyBoard?.snapshot ?? null,
    };
  }

  /** The title card that opens the bonus and says how it plays. */
  private playHoldAndSpinIntro(spins: number): Promise<void> {
    return this.playTitleCard(`${HOLD_AND_SPIN_NAME.toUpperCase()}!`, {
      sub: holdAndSpinIntro(spins),
      sting: BONUS_STING,
      kind: 'win',
    });
  }

  /** The coins and board come on, per-reel holds go off and the road
   *  panel makes way for the board. */
  private beginHoldAndSpin(model: HoldAndSpinModel): void {
    this.holdAndSpinComponent?.bind(model);
    this.machine?.setHoldInteractive(false);
    this.setBonusShown(true);
  }

  /**
   * The end of the bonus: every locked symbol celebrates while the end
   * board says what the coins paid; COLLECT sends the coins flying from
   * each locked cell into the wallet (producer 2026-10-14: coins won fly
   * into the coin counter). Then the slot goes back to its base look.
   */
  private async payHoldAndSpin(model: HoldAndSpinModel, total: number, fullGrid: boolean): Promise<void> {
    const component = this.holdAndSpinComponent;
    const machine = this.machine;
    if (component && machine && total > 0) {
      const cells = model.cellsList();
      void machine.highlightWins([], this.winLines, cells.map((c) => ({ col: c.col, row: c.row })));
      const dialog = this.bonusWinDialog;
      if (dialog) {
        this.app.stage.addChild(dialog);
        if (this.lastCtx) dialog.layout(this.lastCtx.width, this.lastCtx.height);
        await dialog.play({ total, locked: cells.length, fullGrid });
      }
      await this.flyCoinsToWallet(component.coinSources(), total);
    } else if (total > 0) {
      this.model.credit(total, 'collect');
    }
    component?.unbind();
    component?.reset();
    model.dispose();
    this.setBonusShown(false);
  }

  /**
   * Phase D.1 + D.2 — server-authoritative spin path. Kicks off the
   * reel scroll immediately, awaits the {@link spinFn} response in
   * parallel with the min-spin animation, then lands the reels on the
   * server's outcome. Wallet balance + free-spin counter come from the
   * server response (the local model is mirrored, not mutated locally).
   *
   * <p>Returns a {@link RunSpinResult} so the rest of {@link handleSpin}
   * can flow through the same post-spin pipeline (highlight wins,
   * gamble flow, no-win text, free-spin trigger fanfare).
   *
   * <p>Hold-and-spin trigger detection is still suppressed in server
   * mode — D.3 lands the server-side bonus state machine.
   */
  private async runServerSpin(bet: number): Promise<RunSpinResult> {
    if (!this.spinFn || !this.machine) return { ran: false };
    this.model.markSpinStarted();
    let response: SpinSlotResponse;
    try {
      // Fire the server request + start the reel scroll in parallel.
      // {@link spinDriven} blocks on (min-spin-time AND outcome-resolved)
      // so a fast server doesn't cut the spin short, a slow one
      // gracefully extends the scroll.
      const serverPromise = this.spinFn(bet);
      // The server has settled the spin before the reels land, so the
      // last reel lands with coins on a win (scaled by the bet's multiple).
      const outcomePromise = serverPromise.then((r) => {
        // Reel collectibles: a found relic lands as its own painting.
        this.pendingCollect = r.collect ?? null;
        if (r.collect && !this.relics) this.setRelics({ themeId: this.theme.id, state: r.collect });
        const stake = r.settlement.bet > 0 ? r.settlement.bet : bet;
        return {
          ...this.toEngineOutcome(r.outcome.grid),
          winMultiple: stake > 0 ? r.settlement.totalWinnings / stake : 0,
        };
      });
      // Run scrolling concurrently. We don't await spinDriven before the
      // server promise because spinDriven itself awaits the outcome.
      // 2026-06-04 — opt in to client-side hold (respectReelHolds=true)
      // so a player-held reel actually stays in place when SPIN runs.
      // Trade-off: the server still produces a full grid including the
      // held column; settlement uses the server's grid, so a held
      // reel can be visually frozen while paylines pay out against
      // what would have been there. Acceptable for a single-player
      // casual slot; revisit if this slot ever needs strict server +
      // visual parity (e.g. real-money mode).
      await this.machine.spinDriven(outcomePromise, { respectReelHolds: true });
      response = await serverPromise;
    } catch (err) {
      // Server failure — log, force-stop the reels, and bail out.
      // Without forceLandAll(), spinDriven's rejection leaves every
      // reel in isSpinning=true with its ticker handler attached, so
      // the strip keeps scrolling forever. Snapping to idle here lets
      // the user see a stopped (if cosmetically arbitrary) grid plus
      // the failure message, rather than an endless spin.
      console.error('[slot] server spin failed', err);
      this.machine?.forceLandAll();
      if (err instanceof SpinRefusedError && err.code === 'OUT_OF_COINS') {
        this.hud?.setResultText(outOfCoinsText(err.nextRefillAt));
      } else {
        this.hud?.setResultText('⚠️ spin failed — please try again');
      }
      this.model.markSpinLanded();
      return { ran: false };
    }

    this.lastServerSpinId = response.spinId;

    // Phase D.3 — stash the bonus initial state so handleSpin routes
    // to runServerBonusFlow after its post-spin highlights. Server is
    // authoritative for trigger detection; client just renders.
    if (response.holdAndSpin && response.holdAndSpin.phase === 'started') {
      this.pendingServerBonus = response.holdAndSpin;
    }

    // Set the local balance to the *post-debit, pre-credit* value so
    // the take-win animation's per-coin model.credit increments toward
    // the server's authoritative final wallet.coins. Without this
    // adjustment, the local model would double-credit (server already
    // applied -bet + winnings; collectLogic would add winnings again).
    // Math: wallet.coins - totalWinnings = (prev - bet + winnings) -
    // winnings = prev - bet (or just prev for a free spin with no
    // debit). After runCollect, balance = wallet.coins. ✓
    //
    // Royal Road: the coins the trail's squares paid are in that wallet
    // too. Hold them back as well: they fly in from the road once it has
    // rolled to them (payRoadSquares). Any still flying from the spin
    // before are in this wallet, so that flight stops adding them.
    // A completed relic set's coins likewise fly in with its moment, and
    // so do the coins a Sweet Trail hop paid (slots without the road).
    const roadCoins = trailSquareCoins(response.road) + trailStepCoins(response.trail);
    this.dropRoadCoins();
    this.roadCoinsOwed = roadCoins;
    this.model.setBalance(
      response.wallet.coins - response.settlement.totalWinnings - roadCoins - (this.pendingCollect?.completed?.coins ?? 0),
      'spin-bet',
    );
    if (roadCoins > 0) this.payRoadSquares();
    // Mirror the server's free-spin counter — the bet selector + the
    // free-spin chip both subscribe to free-spins-changed and update
    // automatically.
    this.model.setFreeSpinsRemaining(response.freeSpinsRemaining);
    // Emit a free-spins-granted event for the trigger fanfare so audio
    // / visual subscribers can hook into it. Mirrors the local
    // RunSpinLogic flow, just driven by the server's count.
    if (response.freeSpinsGranted > 0) {
      this.model.events.emit({
        kind: 'free-spins-granted',
        count: response.freeSpinsGranted,
        source: 'scatter',
      });
    }
    this.model.markSpinLanded();
    if (this.onServerSpinLanded) {
      try { this.onServerSpinLanded(response); }
      catch (err) { console.warn('[SlotDemoScene] onServerSpinLanded threw; ignoring', err); }
    }

    const engineOutcome = this.toEngineOutcome(response.outcome.grid);
    // Gem cells: the server pays the ones it lists in reelGems (reel_gems),
    // already in this spin's wallet, so those come from its numbers alone.
    // Any other gem cell on the grid is one the server leaves to the app
    // (legacy maths), counted from the theme's gemValue as before.
    const gems = spinGems(engineOutcome, response.reelGems);
    return {
      ran: true,
      wasFreeSpin: response.wasFreeSpin,
      outcome: engineOutcome,
      reelGems: gems.paidGems > 0 ? { cells: gems.paid, gems: gems.paidGems, walletGems: response.wallet.gems } : undefined,
      // Server's settlement is structurally identical to the engine's;
      // a shallow re-shape strips the readonly variance + null-vs-missing
      // scatter so TypeScript accepts it as the local SpinSettlement.
      settlement: {
        totalWinnings: response.settlement.totalWinnings,
        bet: response.settlement.bet,
        net: response.settlement.net,
        settlements: response.settlement.settlements.map((s) => ({
          lineId: s.lineId,
          matchSymbolId: s.matchSymbolId,
          matchCount: s.matchCount,
          hadWild: s.hadWild,
          winnings: s.winnings,
        })),
        scatter: response.settlement.scatter
          ? {
              scatterCount: response.settlement.scatter.scatterCount,
              matchSymbolId: response.settlement.scatter.matchSymbolId,
              winnings: response.settlement.scatter.winnings,
              positions: response.settlement.scatter.positions.map((p) => ({
                col: p.col,
                row: p.row,
              })),
            }
          : null,
        gemsAwarded: gems.unpaidGems,
        gemPositions: gems.unpaid.map((c) => ({ col: c.col, row: c.row })),
      },
      freeSpinsGranted: response.freeSpinsGranted,
    };
  }

  /** Map a server symbol-id grid to the engine's full {@code SpinOutcome}
   * shape. Looks up each id in the active theme's symbol roster; falls
   * back to the first symbol if the id is unknown (defensive — should
   * never happen because the server uses the same symbol catalog).
   *
   * <p>Artefact localisation (2026-06-06): the server pool carries a
   * single biome-agnostic {@code 'artefact'} symbol (it has no idea
   * which biome the client is rendering). Here we remap that generic
   * id to THIS theme's own artefact def — the one symbol in the pool
   * that declares an {@code artefactId}. That's what structurally
   * enforces "one artefact per biome": a Greendale slot can only ever
   * resolve the generic drop to Excalibur, never another biome's
   * relic. Themes with no artefact (none, currently) fall through to
   * the normal lookup, so the generic id would hit the fallback —
   * harmless. */
  private toEngineOutcome(grid: readonly (readonly string[])[]): SpinOutcome {
    const symbolsById = new Map(this.theme.slot.symbols.map((s) => [s.id, s]));
    const fallback = this.theme.slot.symbols[0]!;
    const biomeArtefact = this.theme.slot.symbols.find(
      (s) => s.artefactId !== undefined && s.artefactId !== '',
    );
    const find = this.pendingCollect?.find;
    const relic = find ? this.relics?.relicAt(find.piece) ?? null : null;
    const relicSymbol = relic ? this.relicSymbol(relic) : null;
    const resolve = (id: string): SlotSymbolDef => {
      if (id === GENERIC_ARTEFACT_SYMBOL_ID && relicSymbol) return relicSymbol;
      if (id === GENERIC_ARTEFACT_SYMBOL_ID && biomeArtefact) return biomeArtefact;
      return symbolsById.get(id) ?? fallback;
    };
    return {
      grid: grid.map((reel) => reel.map((id) => resolve(id))),
    };
  }

  private scatterGlyph(): string {
    const id = this.theme.slot.scatterSymbolId;
    if (!id) return '🎯';
    return this.theme.slot.symbols.find((s) => s.id === id)?.glyph ?? '🎯';
  }

  /** Run the take-win flying-coin animation. Called by GambleFlow when the
   * player chooses to collect. CollectLogic also clears the model's
   * pendingWinnings as part of its execute.
   *
   * <p>Producer audit Phase 4: source coordinates now come from each
   * cell that contributed to the win (captured in {@link handleSpin}
   * as {@link lastWinningCellPositions}) — so coins spawn from each
   * winning symbol and fly to the wallet HUD's coin readout. Falls
   * back to the burst centre when no winning cells were captured
   * (e.g. wins through the collect path that didn't go via
   * handleSpin, defensive). */
  private async runCollect(): Promise<void> {
    if (!this.takeWin || !this.winSequence || !this.hud || !this.collectLogic) {
      return;
    }
    const sources: ReadonlyArray<{ x: number; y: number }> =
      this.lastWinningCellPositions.length > 0
        ? this.lastWinningCellPositions
        : [this.winSequence.burstPosition];

    // Producer audit Phase 4 — scale coin count with the win's tier
    // ratio (totalWinnings / bet). Bigger wins → more coins flying
    // (capped at 48 so big jackpots stay tractable). The win amount
    // itself drives the BASE count via {@code Math.ceil(total / 50)}
    // inside TakeWinAnimation; we multiply the cap so the formula's
    // upper bound matches the celebration size.
    const winRatio =
      this.model.currentBet > 0
        ? this.model.pendingWinnings / this.model.currentBet
        : 0;
    const tierCoinCap =
      winRatio >= 50 ? 48 // JACKPOT
        : winRatio >= 25 ? 40 // SUPER MEGA
        : winRatio >= 10 ? 34 // MEGA
        : winRatio >= 5 ? 26 // BIG
        : 18; // standard
    await this.collectLogic.execute({
      takeWin: this.takeWin,
      source: sources,
      target: this.hud.balanceLabelPosition,
      onCoinArriveSideEffect: () => this.hud?.bumpBalance(),
      maxCoins: tierCoinCap,
    });
    // Cell positions are stale once the win has been collected. Clear
    // so a subsequent zero-winnings collect (defensive path) falls
    // back to the burst centre.
    this.lastWinningCellPositions = [];
  }

  /**
   * Queue an item-drop cycle-and-settle reveal at a Reel-Race grid
   * cell. Called by the mobile host ({@code WheelReelRaceView}) on
   * the SSE delta that surfaces {@code lastSpinItemCell + new loadout
   * item}; the engine doesn't know about {@code ItemId} so the host
   * builds the rotation list + the final landing def from the item
   * asset aliases ({@code item:{ID}}) and passes them in.
   *
   * <p>{@code cell} is the 0..14 row-major index the server picks
   * (5 cols × 3 rows). The actual animation fires when the reel
   * lands — see the consumer in the post-spin handler. The returned
   * promise resolves when the cycle + settle completes so the host
   * can chain a chip-fly arc to the BalancePill / inventory.
   *
   * <p>No-op when the machine isn't mounted or the cell is out of
   * range. If a prior reveal is still queued (rare — would mean two
   * SSE deltas arrived between spins), the previous resolver is
   * resolved-and-replaced so the host's await doesn't hang.
   */
  async playItemDropReveal(
    cell: number,
    finalSymbolDef: SlotSymbolDef,
    cycleSymbolDefs: readonly SlotSymbolDef[],
  ): Promise<void> {
    if (!this.machine) return;
    if (cell < 0 || cell > 14) return;
    // Drain any stale pending reveal before replacing — defensive
    // against double-fire from rapid SSE deltas; in practice a single
    // spin produces a single drop, so this branch should never hit.
    const stale = this.pendingItemDropReveal;
    if (stale) stale.resolve();
    return new Promise<void>((resolve) => {
      this.pendingItemDropReveal = {
        cell,
        finalDef: finalSymbolDef,
        cycleDefs: cycleSymbolDefs,
        resolve,
      };
    });
  }

  /** True when spins go to the server. A first visit that opens before
   *  the session is ready plays locally until {@link refreshSpinMode}
   *  switches it over. */
  get serverMode(): boolean {
    return this.serverSpins;
  }

  /** Switch a local-mode scene to server spins if the session is now
   *  available. Only while idle and outside a free-spin run, so a bonus
   *  never straddles the two modes; the first server spin then sets the
   *  balance from the server wallet. Called at the start of each spin and
   *  by the host when its spin function arrives. */
  refreshSpinMode(): void {
    if (this.serverSpins || this.spinFn === null || this.spinning) return;
    if (this.model.freeSpinsRemaining > 0) return;
    if (this.serverSpinAvailable && !this.serverSpinAvailable()) return;
    this.serverSpins = true;
    try { this.onServerModeChange?.(); }
    catch (err) { console.warn('[SlotDemoScene] onServerModeChange threw; ignoring', err); }
  }

  /** Take the profile's balance when it moved on its own. The model reads
   *  the profile once, at construction, but the profile keeps changing
   *  after that: the boot wallet sync corrects the local placeholder to the
   *  server wallet, and the daily bonus lands on the server wallet. Without
   *  this a slot built before those landed spun from the stale value, and
   *  its first spin pushed that value back into the profile (the first-visit
   *  "1,100 coins, then 475 after one spin" bug). Skipped mid-spin, where
   *  the spin owns the balance; the next spin picks the change up. The
   *  model's own changes come back here already equal, so this never loops. */
  private followProfileBalance(): void {
    if (!this.profile || this.spinning) return;
    if (this.profile.balance === this.model.balance) return;
    // The profile's balance is the server's, road coins included.
    this.dropRoadCoins();
    this.model.setBalance(this.profile.balance, 'session-grant');
  }

  /** Set the balance from the server wallet outside a spin (Sweet Trail
   *  bonus prizes are credited server-side between spins). */
  syncBalance(coins: number): void {
    this.dropRoadCoins();
    this.model.setBalance(coins, 'session-grant');
  }

  /** Set the balance from the server wallet's `coins` between spins, when
   *  they already hold a win still waiting to be collected: the counter
   *  shows that win only once it's collected, so it isn't counted twice. */
  syncWalletCoins(coins: number): void {
    this.syncBalance(coins - this.model.pendingWinnings);
  }

  /** Multiplayer Reel Race — update the 3-slot loadout strip's
   *  visible contents. Each entry is an asset alias (e.g.
   *  {@code "item:BOOST"}) or null for an empty slot. No-op when
   *  {@link SlotDemoSceneOptions#showLoadoutStrip} is false. */
  setLoadoutSlots(aliases: ReadonlyArray<string | null>): void {
    this.hud?.loadoutStrip?.setSlots(aliases);
  }

  /** Multiplayer Reel Race — set the pending-claim state.
   *  {@code iconAlias} is the incoming item's icon (null clears),
   *  {@code label} is shown in the "TAP TO REPLACE …" badge below
   *  the strip. */
  setLoadoutPending(iconAlias: string | null, label: string = ''): void {
    this.hud?.loadoutStrip?.setPending(iconAlias, label);
  }

  /** Multiplayer Reel Race — fade the strip out while the player is
   *  mid-spin and back in when the spin settles. Internal fade
   *  animation is driven by the scene's ticker; this just toggles
   *  the target. */
  setLoadoutHidden(hidden: boolean): void {
    this.hud?.loadoutStrip?.setHidden(hidden);
  }

  /** Forward the React BalancePill's live coin + gem chip centres (the
   *  "position bridge") to the HUD so the take-win animation lands on the
   *  real chips regardless of the pill's layout. Null = fall back to the
   *  computed targets. */
  // -------- Royal Road --------

  /** Keep `top` and `bottom` pixels clear above and below the reels for the
   *  Royal Road panel and trail; null returns to the standard layout. */
  setChromeInsets(insets: { top: number; bottom: number } | null): void {
    this.chromeInsets = insets;
    this.layout();
  }

  /** Show the side lights' Ready meter (whole lights lit and how far the
   *  next has filled); null hides them. */
  setSideLights(state: { lit: number; next: number } | null): void {
    this.pendingSideLights = state;
    this.sideLights?.setReady(state);
  }

  setSideLightsMode(mode: SideLightsMode): void {
    this.sideLights?.setMode(mode);
  }

  /** Royal Road: a friend has just joined. They say hello over the slot,
   *  once a landmark's restore card and moment have gone (restoring a
   *  landmark is what brings its friend); resolves with 'play' (their free
   *  first game) or 'later'. */
  async showFriendJoin(friend: FriendInfo, freePlay: boolean, title?: string): Promise<FriendJoinChoice> {
    return (await this.showGreeting(friend, freePlay, title ?? `${friend.name.toUpperCase()} JOINS YOU`)) ?? 'later';
  }

  /** The Wheel Run: a star has lit a lamp on this slot. Its card names the
   *  new game and, while its first game is free, offers it; resolves with
   *  'play' or 'later'. `again`: offering it once more from the lamp. */
  async showLampLit(game: LampGameInfo, freePlay: boolean, again = false): Promise<FriendJoinChoice> {
    const card = lampLitCard(game, again);
    const face = { id: game.lamp, lit: true, game: game.game };
    const greeting: Greeting = { portrait: { lamp: face }, line: card.line, lamp: { face, title: game.title, sub: card.sub } };
    return (await this.showGreeting(greeting, freePlay, card.title)) ?? 'later';
  }

  /** A one-off card over the slot (`title`, then `line` under the slot's
   *  Wheel Run ring, or a lit lamp before the ring has loaded), with one
   *  button; resolves once the player has closed it (never, if the slot
   *  goes first, so a card that never showed isn't counted as seen). */
  async showNotice(title: string, line: string): Promise<void> {
    if (!(await this.whenStarted())) return new Promise(() => undefined);
    const ring = this.theme.assets?.road?.textures?.road_ring?.alias;
    const portrait = ring && AssetLoader.shared.getTexture(ring) ? { alias: ring } : { lamp: { id: 'lamp1' as const, lit: true } };
    if ((await this.showGreeting({ portrait, line }, false, title)) === null) return new Promise(() => undefined);
  }

  /** Resolves once the scene is on its canvas (true), or has closed first
   *  (false). The host hears of a scene as it's made, before it starts. */
  private async whenStarted(): Promise<boolean> {
    while (this.state === 'created' || this.state === 'initializing') await new Promise((r) => setTimeout(r, 100));
    return this.state === 'initialized';
  }

  /** The slot this scene plays (its theme's id). */
  get themeId(): string {
    return this.theme.id;
  }

  /** Show a hello, a new game's card or a notice; resolves with the
   *  player's choice, or null when the slot closed before it could show.
   *  One at a time: a card asked for while another is up waits for it. */
  private async showGreeting(greeting: Greeting | FriendInfo, freePlay: boolean, title: string): Promise<FriendJoinChoice | null> {
    // The slot closed meanwhile (or before it started): no hello.
    if (!(await this.whenStarted())) return null;
    while ((this.landmarkRoad?.blocking || this.friendJoin?.visible) && this.app?.stage) {
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    if (!this.app?.stage) return null;
    if (!this.friendJoin) {
      const alias = this.theme.assets?.scene?.columnBackground?.alias;
      this.friendJoin = new FriendJoinDialog(
        (name) => AssetLoader.shared.getTexture(name),
        alias ? AssetLoader.shared.getTexture(alias) : null,
      );
    }
    this.app.stage.addChild(this.friendJoin);
    this.friendJoin.layout(this.app.screen.width, this.app.screen.height);
    AudioBus.shared.play(ROAD_SOUNDS.collected, { volume: 0.7 });
    // The overlays above the canvas step aside while the friend is here.
    const release = this.coverSlot();
    return this.friendJoin.greet(greeting, freePlay, title).finally(release);
  }

  /**
   * The TV title card (big type, a line under it, a sting) before a run or
   * a game, or for a win, something completed or a story beat; `kind` picks
   * its look (see TitleCard). Resolves when it has gone; a tap skips the
   * hold.
   */
  playTitleCard(text: string, options: TitleCardOptions = {}): Promise<void> {
    if (!this.titleCard) return Promise.resolve();
    // Above anything added since the scene was built.
    this.app.stage.addChild(this.titleCard);
    const release = this.coverSlot();
    return this.titleCard.play(text, options).finally(release);
  }

  /**
   * Coins a road payout or a game won fly from `from` (canvas pixels) into
   * the top bar's coin counter, as reel wins do. With `credit` (the
   * default) each coin adds its share to the balance as it lands, so call
   * this before reconciling with the server's wallet, not after.
   */
  async flyCoinsToWallet(
    from: { x: number; y: number } | ReadonlyArray<{ x: number; y: number }>,
    amount: number,
    credit: boolean | ((value: number) => void) = true,
  ): Promise<void> {
    if (amount <= 0) return;
    const add = credit === true ? (value: number) => this.model.credit(value, 'collect') : credit || null;
    if (!this.takeWin || !this.hud) {
      add?.(amount);
      return;
    }
    // Keep the coins above anything added since the scene was built.
    this.app.stage.addChild(this.takeWin);
    await this.takeWin.takeWin(
      amount,
      from,
      this.hud.balanceLabelPosition,
      (value) => {
        add?.(value);
        this.hud?.bumpBalance();
      },
      { totalDurationMs: 1500, flyDurationMs: 700, maxCoins: Math.min(30, 10 + Math.round(Math.log2(1 + amount))) },
    );
  }

  /**
   * The coins the road's squares paid on the last spin fly from the road's
   * token into the coin counter once the road has rolled to them, adding
   * themselves to the balance as they land, as reel wins do. With the road
   * off screen they're just added.
   */
  private payRoadSquares(): void {
    const key = this.roadPayKey;
    const pay = (value: number) => {
      if (key !== this.roadPayKey) return;
      const coins = Math.min(value, this.roadCoinsOwed);
      if (coins <= 0) return;
      this.roadCoinsOwed -= coins;
      this.model.credit(coins, 'collect');
    };
    // The Sweet Trail: they fly from its prize plate when it comes up,
    // after the reels' win (or straight in if it never does).
    const trail = this.trailChrome;
    if (!this.roadPanel && trail) {
      const fly = (from: { x: number; y: number } | null): void => {
        trail.onPrize = null;
        if (this.roadPayTimer) clearTimeout(this.roadPayTimer);
        this.roadPayTimer = null;
        const coins = this.roadCoinsOwed;
        if (key !== this.roadPayKey || coins <= 0) return;
        if (!from) {
          pay(coins);
          return;
        }
        this.roadPrizeFly = { coins, from };
        void this.flyCoinsToWallet(from, coins, pay).then(() => pay(this.roadCoinsOwed));
      };
      trail.onPrize = fly;
      this.roadPayTimer = setTimeout(() => fly(null), 6000);
      return;
    }
    this.roadPayTimer = setTimeout(() => {
      this.roadPayTimer = null;
      const coins = this.roadCoinsOwed;
      if (key !== this.roadPayKey || coins <= 0) return;
      const panel = this.roadPanel;
      if (!panel || panel.destroyed || !panel.visible) {
        pay(coins);
        return;
      }
      const from = panel.prizePoint;
      this.roadPrizeFly = { coins, from };
      // Whatever rounding left over lands with the last coin.
      void this.flyCoinsToWallet(from, coins, pay).then(() => pay(this.roadCoinsOwed));
    }, TAKEN_MS);
  }

  /** Forget road coins still owed: the balance about to be set is the
   *  server's, which counts them. */
  private dropRoadCoins(): void {
    this.roadPayKey++;
    this.roadCoinsOwed = 0;
    if (this.roadPayTimer) clearTimeout(this.roadPayTimer);
    this.roadPayTimer = null;
  }

  /**
   * Play one of the road's mini-games (Pick a prize, ...) over everything:
   * its title card, then its board. Resolves when the player closes it,
   * with what it paid; a wallet prize's coins have already flown into the
   * coin counter, so reconcile with the server's wallet after. A friend's
   * game is under `title`, its own name on this road.
   */
  playMiniGame(offer: PlayOffer, api: PlayApi, title?: string): Promise<MiniGameEnd> {
    const layer = this.miniGameLayer();
    this.app.stage.addChild(layer);
    const release = this.coverSlot();
    return layer.play(offer, api, title).finally(release);
  }

  /** Whether the scene can play a game of this kind. */
  playsMiniGame(kind: string): boolean {
    return this.miniGameLayer().plays(kind);
  }

  /** The mini-game on screen as plain data (browser tests), or null. */
  get miniGameUi(): MiniGameSnapshot | null {
    return this.miniGames?.snapshot ?? null;
  }

  /** Press a mini-game's button by its label (browser tests). */
  pressMiniGame(label: string): boolean {
    return this.miniGames?.press(label) ?? false;
  }

  /** Tap a mini-game's piece by index (browser tests). */
  tapMiniGame(index: number): boolean {
    return this.miniGames?.tap(index) ?? false;
  }

  private miniGames: MiniGameLayer | null = null;
  private gameArt: ((kind: string, themeId: string) => Promise<void>) | null = null;

  /** How a mini-game's art and sounds load on this slot: the host fetches
   *  them on first use, so a game plays on a slot without the road (whose
   *  theme has no game art) with its art and sounds. */
  setGameArt(load: ((kind: string, themeId: string) => Promise<void>) | null): void {
    this.gameArt = load;
  }

  private miniGameLayer(): MiniGameLayer {
    if (this.miniGames) return this.miniGames;
    const loader = AssetLoader.shared;
    const layer = new MiniGameLayer({
      // A slot or its world can paint its own pieces for a game (a card
      // back, its moles): those are `<alias>@<theme id>`, the slot's own
      // first, then its world's (named by the world's road slot), and the
      // shared piece stands in for both.
      texture: (alias) =>
        loader.getTexture(`${alias}@${this.theme.id}`) ??
        loader.getTexture(`${alias}@${worldOf(this.theme.id)}`) ??
        loader.getTexture(alias),
      floor: () => {
        const alias = this.theme.assets?.scene?.columnBackground?.alias;
        return alias ? loader.getTexture(alias) : null;
      },
      picture: (index) => {
        // The slot's own symbols, lowest first: the first four plain ones
        // and the top one (wild, scatter and specials left out).
        const alias = gamePictureSymbols(this.theme.slot)[index]?.textureAlias;
        return alias ? loader.getTexture(alias) : null;
      },
      flyCoins: (from, amount) => this.flyCoinsToWallet(from, amount),
      titleCard: (text, options) => this.playTitleCard(text, options),
      // The host's loader when it gave one (any slot: the Sweet Trail's
      // games), else the road's art on the theme.
      loadArt: (kind) => (this.gameArt ? this.gameArt(kind, this.theme.id) : loader.loadThemeRoadGame(this.theme, kind)),
    });
    // It keeps itself sized to the screen, and lets go when it's destroyed.
    const renderer = this.app.renderer;
    const fit = () => {
      if (layer.destroyed) renderer.off('resize', fit);
      else layer.layout(renderer.width, renderer.height);
    };
    renderer.on('resize', fit);
    layer.on('destroyed', () => renderer.off('resize', fit));
    fit();
    this.miniGames = layer;
    return layer;
  }

  /** Royal Road: free spins a Lamp Run paid; the server already added them
   *  to the session, and the next spin's response confirms the count. */
  addFreeSpins(count: number): void {
    if (count > 0) this.model.setFreeSpinsRemaining(this.model.freeSpinsRemaining + count);
  }

  /** What SPIN means: 'spin' normally; STOP, landing, COLLECT or DONE
   *  during a Lamp Run. */
  setSpinMode(mode: SpinMode): void {
    if (mode !== 'spin') this.hud?.setSpinning(false);
    this.hud?.setSpinMode(mode);
  }

  /** Show the road's landmarks under the SPIN row (null hides them). */
  setLandmarkRoad(view: LandmarkRoadView | null): void {
    this.pendingLandmarkRoad = view;
    this.landmarkRoad?.setView(view);
  }

  /** Reel collectibles: the slot's relics on the action row (null hides them).
   *  Never in the Reel Race (showLoadoutStrip): relics are single-player. */
  setRelics(view: RelicsView | null): void {
    if (this.showLoadoutStrip) return;
    this.pendingRelics = view;
    if (view && !this.relics) this.buildRelics();
    this.relics?.setView(view);
  }

  /** The relics as plain data (browser tests), or null when they're off. */
  get relicsUi(): RelicsComponent['snapshot'] | null {
    return this.relics?.visible ? this.relics.snapshot : null;
  }

  /** Open the relics board, as a tap on the shelf does (browser tests). */
  openRelics(): void {
    void this.relics?.openBoard();
  }

  /** Press a button on the relics board by its label (browser tests). */
  pressRelics(label: string): boolean {
    return this.relics?.press(label) ?? false;
  }

  private buildRelics(): void {
    if (!this.hud || !this.app?.stage) return;
    const loader = AssetLoader.shared;
    const relics = new RelicsComponent({
      bezel: () => loader.getTexture('road:light_bezel'),
      relic: (alias) => loader.getTexture(alias),
      floor: () => this.floorTexture(),
      coin: () => loader.getTexture('shared:icon:coin'),
      gem: () => loader.getTexture('shared:icon:gem'),
      actionGap: () => this.hud?.actionGap ?? null,
      showFind: (find) => this.showFind(find),
      showBoard: (board, play) => this.showBoard(board, play),
      titleCard: (text, options) => this.playTitleCard(text, options),
      flyCoins: (from, amount) => this.flyCoinsToWallet(from, amount),
      flyGems: (from, amount) => this.flyGemsToWallet(from, amount),
      canOpen: () => this.idle && !this.boardOpen,
    });
    const stage = this.app.stage;
    stage.addChildAt(relics, stage.getChildIndex(this.hud) + 1);
    stage.addChild(relics.overlay);
    this.relics = relics;
    if (this.lastCtx) relics.layout(this.lastCtx);
  }

  /** A relic's reel symbol: its painting, and no part in the old app-side finds. */
  private relicSymbol(relic: Relic): SlotSymbolDef {
    const id = relicSymbolId(relic);
    let def = this.relicSymbols.get(id);
    if (!def) {
      def = { id, label: relic.name, glyph: relic.glyph, tint: 0xf5d67a, weight: 0, textureAlias: relic.art };
      this.relicSymbols.set(id, def);
    }
    return def;
  }

  /** A spin's relics once its reels have landed: the cell pulses and the
   *  relic flies to the shelf (after a win's highlight), then its moment. */
  private async playRelics(collect: CollectState, won: boolean): Promise<void> {
    const relics = this.relics;
    if (!relics || !this.machine) return;
    const cells = collect.find?.cells ?? [];
    let from: { x: number; y: number } | null = null;
    const first = cells[0];
    if (first) {
      this.machine.playBonusCollect(cells.map((c) => ({ col: c.col, row: c.row })));
      const cc = this.machine.cellCenter(first.col, first.row);
      const scale = this.machine.scale.x;
      from = { x: this.machine.x + cc.x * scale, y: this.machine.y + cc.y * scale };
      if (won) await new Promise<void>((resolve) => setTimeout(resolve, 900));
    }
    this.app.stage.addChild(relics.overlay);
    await relics.play(collect, from);
  }

  /**
   * The gem cells' moment, once the reels have landed with gem cells the
   * server paid (reel_gems): each cell pulses and lights up with its "+N"
   * over it, then its gems (one icon a gem) lift off and fly to the
   * wallet's gem count, which counts them as the first ones arrive, with
   * its "+N" toast. Nothing here is awaited: the next spin (a free spin
   * too) starts when it would anyway and puts the light out. The count
   * runs on a timer, not the flight, so the gems still count if the slot
   * closes while they fly; it takes {@link gemsToCount} then, so a wallet
   * sync in between can't make it count them twice.
   */
  private playGemCells(moment: GemCellsMoment): void {
    // Gem cells the server didn't pay (local spins, the reel race, legacy
    // maths) play as they did before reel_gems.
    if (!moment.paid) {
      this.playOwnGemCells(moment);
      return;
    }
    const { cells } = moment;
    const profile = this.profile;
    // Counted even when the reels have gone (the slot closed as they
    // landed): the server has paid these gems either way.
    setTimeout(() => {
      const counted = gemsToCount(moment, profile?.gems ?? 0);
      if (counted > 0) profile?.awardGems(counted);
      this.lastGemCells = { cells: cells.map((c) => ({ ...c })), counted };
    }, GEM_LIFT_MS + GEM_FLY_MS);
    if (!this.machine) return;
    this.machine.playBonusCollect(cells);
    const stage = this.app.stage;
    const machine = this.machine;
    const scale = machine.scale.x;
    const at = (c: GemCell) => {
      const cc = machine.cellCenter(c.col, c.row);
      return { x: machine.x + cc.x * scale, y: machine.y + cc.y * scale };
    };
    this.gemGlow ??= new GemCellGlow();
    // Over the reels' frame too, so a top row cell's "+N" shows as it rises.
    const under = this.borderSprite?.parent === stage ? this.borderSprite : machine;
    stage.addChildAt(this.gemGlow, stage.getChildIndex(under) + 1);
    const size = machine.cellSize() * scale;
    void this.gemGlow.play(cells.map((c) => ({ ...at(c), size, gems: c.gems })));
    const from = cells.map(at);
    setTimeout(() => {
      const takeWin = this.takeWin;
      if (!takeWin || takeWin.destroyed || !this.hud) return;
      const target = this.hud.gemLabelPosition;
      // One flight a cell, so a 10 gem cell sends ten gems and a 5 sends five.
      cells.forEach((c, i) => {
        void takeWin.takeWin(c.gems, [from[i]!], target, () => undefined, {
          iconAlias: 'shared:icon:gem',
          iconCount: Math.min(12, c.gems),
          totalDurationMs: GEM_FLY_MS * 2,
          flyDurationMs: GEM_FLY_MS,
        });
      });
    }, GEM_LIFT_MS);
  }

  /**
   * Gem cells the app pays itself, from their own values (local spins, the
   * reel race, legacy maths): counted at once, the cells pulse and their
   * gems fly to the wallet's gem count, as before reel_gems.
   */
  private playOwnGemCells(moment: GemCellsMoment): void {
    this.profile?.awardGems(moment.own);
    this.lastGemCells = { cells: moment.cells.map((c) => ({ ...c })), counted: moment.own };
    const machine = this.machine;
    if (!machine) return;
    machine.playBonusCollect(moment.cells);
    const takeWin = this.takeWin;
    if (!takeWin || !this.hud) return;
    const scale = machine.scale.x;
    const target = this.hud.gemLabelPosition;
    // One flight a cell, so a 10 gem cell sends ten gems and a 5 sends five.
    for (const c of moment.cells) {
      const cc = machine.cellCenter(c.col, c.row);
      void takeWin.takeWin(c.gems, [{ x: machine.x + cc.x * scale, y: machine.y + cc.y * scale }], target, () => undefined, {
        iconAlias: 'shared:icon:gem',
        iconCount: c.gems,
        totalDurationMs: GEM_FLY_MS * 2,
        flyDurationMs: GEM_FLY_MS,
      });
    }
  }

  /** The gem cells' moment as plain data (browser tests): the glow now and
   *  the last moment's cells and the gems it counted. */
  get gemCellsUi(): { glow: GemCellGlowSnapshot | null; last: { cells: GemCell[]; counted: number } | null } {
    return { glow: this.gemGlow?.snapshot ?? null, last: this.lastGemCells };
  }

  /** Gems paid on the server fly from `from` to the gem counter, which
   *  counts them in as they land. */
  private async flyGemsToWallet(from: { x: number; y: number }, amount: number): Promise<void> {
    if (amount <= 0) return;
    this.profile?.awardGems(amount);
    if (!this.takeWin || !this.hud) return;
    this.app.stage.addChild(this.takeWin);
    await this.takeWin.takeWin(amount, [from], this.hud.gemLabelPosition, () => undefined, {
      iconAlias: 'shared:icon:gem',
      iconCount: Math.min(12, amount),
      totalDurationMs: 1400,
      flyDurationMs: 700,
    });
  }

  /** The landmarks as drawn, for browser tests. */
  get landmarkRoadDebug(): ReturnType<LandmarkRoadComponent['debug']> | null {
    return this.landmarkRoad?.debug() ?? null;
  }

  /** Put a Wheel Run (new or resumed) over the reels. A new one opens with
   *  its TV title card (full side lights flash first). */
  startBoardRun(run: BoardRunState): void {
    this.landmarkRoad?.setEnabled(false);
    const fresh = run.stops === 0;
    if (!fresh) {
      this.boardRun?.start(run);
      this.sideLights?.setMode('chase');
      return;
    }
    const lights = run.origin === 'ready' && !run.lucky;
    if (lights) this.sideLights?.setMode('flash');
    const title = `${(run.lucky ? LUCKY_RUN_NAME : RUN_NAME).toUpperCase()}!`;
    const sub = run.lucky
      ? `${run.stopsMax} stops, no bombs`
      : lights
        ? `The lights are full! ${run.stopsMax} stops, ${run.livesMax} lives`
        : `End of the road! ${run.stopsMax} stops, ${run.livesMax} lives`;
    AudioBus.shared.play(ROAD_SOUNDS.sweep, { volume: 0.7 });
    this.runIntro = this.playTitleCard(title, { sub, sting: ROAD_SOUNDS.runStart, kind: 'run' }).then(() => {
      this.boardRun?.start(run, true);
      this.sideLights?.setMode('chase');
    });
  }

  /** Chase onto the cell the server picked; resolves when it has landed. */
  async boardLandOn(result: BoardStopResult): Promise<void> {
    await this.runIntro;
    if (!this.boardRun) return;
    await this.boardRun.landOn(result.cell);
    this.boardRun.showResult(result);
    if (result.prize.coins > 0 || result.prize.freeSpins > 0) this.sideLights?.setMode('flash');
  }

  /** Show the run pot after a lit lamp's game paid into it. */
  boardShowPot(pot: RunPot): void {
    this.boardRun?.showPot(pot);
  }

  /**
   * A game won the gold pot: its TV title card, then (a lamp's game) its
   * coins fly from the middle of the screen into the run pot on the ring.
   * A game paying the wallet has flown its coins there already.
   */
  async goldPotWon(coins: number, intoRun: boolean): Promise<void> {
    const card = goldPotCard(coins, intoRun);
    await this.playTitleCard(card.title, { sub: card.sub, sting: ROAD_SOUNDS.collected, holdMs: 1500, kind: 'win' });
    const ctx = this.lastCtx;
    if (!intoRun || !this.boardRun?.visible || !this.takeWin || !ctx) return;
    this.app.stage.addChild(this.takeWin);
    await this.takeWin.takeWin(coins, { x: ctx.width / 2, y: ctx.height / 2 }, this.boardRun.potCentre, () => undefined, {
      totalDurationMs: 1500,
      flyDurationMs: 700,
      maxCoins: Math.min(30, 10 + Math.round(Math.log2(1 + coins))),
    });
  }

  /** Set the light going again for the next STOP. */
  boardResume(): void {
    this.boardRun?.resume();
    this.sideLights?.setMode('chase');
  }

  /**
   * Where the road's panel goes: its mode, top and height (the host works
   * these out with the chrome insets); the scene centres it on the reel
   * frame, as wide as the frame up to `maxWidth`. Null takes it away.
   */
  setRoadChrome(layout: { mode: RoadPanelMode; top: number; height: number; maxWidth: number } | null): void {
    this.roadChrome = layout;
    if (this.roadPanel) this.roadPanel.visible = this.panelShowing();
    if (this.lastCtx) this.layoutRoadChrome(this.lastCtx);
  }

  /** Say something over the road panel's road (a gift that couldn't open
   *  just now). Nothing while the panel is put away. */
  sayOnRoad(text: string): void {
    if (this.roadPanel && !this.roadPanel.destroyed && this.roadPanel.visible) this.roadPanel.sayOnRoad(text);
  }

  /** The road after a spin (`spinKey` changes) or a fetch. */
  setRoadState(road: RoadState | null, spinKey: number): void {
    this.roadNow = { road, spinKey };
    if (!this.roadPanel) return;
    if (road) {
      this.roadPanel.setRoad(road, spinKey);
      // The first light: say what the lights are for, once.
      if (road.ready > 0 && !this.lightsExplained && !readFlag(LIGHTS_FLAG)) {
        this.lightsExplained = true;
        writeFlag(LIGHTS_FLAG);
        setTimeout(() => this.explainLights(null), 1600);
      }
    }
    this.roadPanel.visible = this.panelShowing();
  }

  /** The panel shows when there's a road, room for it, and no run or
   *  bonus on. */
  private panelShowing(): boolean {
    return this.roadNow.road !== null && this.roadChrome !== null && !this.roadChromeHidden && !this.runShown && !this.bonusShown;
  }

  /** The hold-and-spin board on or off: it takes the road panel's place
   *  while it's on, and the host's overlays make way. */
  private setBonusShown(shown: boolean): void {
    this.bonusShown = shown;
    if (this.roadPanel) this.roadPanel.visible = this.panelShowing();
    if (this.lastCtx) this.layoutRoadChrome(this.lastCtx);
    if (shown && !this.bonusCover) this.bonusCover = this.coverSlot();
    if (!shown) {
      this.bonusCover?.();
      this.bonusCover = null;
    }
  }

  /**
   * A pop-up, title card or bonus on the canvas covers the slot: the
   * host's overlays above the canvas (Play with others, the trail) make way
   * until the returned release is called, so nothing draws over it.
   * Overlapping covers count, and the host hears only the first and last.
   */
  private coverSlot(): () => void {
    this.covers += 1;
    if (this.covers === 1) {
      this.onCanvasPopupChange?.(true);
      this.trailChrome?.setCovered(true);
      // The slot's name doesn't stay over a card opened as the slot opens
      // (it sits above the road's cards, which are built before it plays).
      this.nameFlash?.hide();
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.covers = Math.max(0, this.covers - 1);
      if (this.covers === 0) {
        this.onCanvasPopupChange?.(false);
        this.trailChrome?.setCovered(false);
      }
    };
  }

  /** The run bar: the run as it stands and its status line, or null when
   *  no run is on (the panel comes back). */
  setRunInfo(view: BoardRunView | null, status: string, notice = false): void {
    this.runNow = { view, status, notice };
    if (!this.runBar || !this.roadPanel) return;
    this.runShown = view !== null && view.phase !== 'idle';
    if (view && this.runShown) this.runBar.setRun(view, status, notice);
    this.runBar.visible = this.runShown && !this.roadChromeHidden;
    this.roadPanel.visible = this.panelShowing();
    if (this.lastCtx) this.layoutRoadChrome(this.lastCtx);
  }

  /** Hide the road's chrome (a slot loading, the first-spin hint). */
  setRoadChromeHidden(hidden: boolean): void {
    this.roadChromeHidden = hidden;
    if (this.runBar) this.runBar.visible = this.runShown && !hidden;
    if (this.roadPanel) this.roadPanel.visible = this.panelShowing();
  }

  /**
   * The Sweet Trail on a slot without the road (Royal Road slots have the
   * road panel instead): its state after a spin (`spinKey` changes) or a
   * fetch; null takes it away. `revealDelayMs` holds the board back while
   * the spin's win shows.
   */
  setTrail(trail: SlotTrailState | null, spinKey: number, revealDelayMs = 0): void {
    if (!trail && !this.trailChrome) return;
    this.trailComponent().setTrail(trail, spinKey, revealDelayMs);
  }

  /** Hide the trail (a slot loading, the first-spin hint, a gamble), what
   *  its collectors are called ("gumdrops") and a line under its board in
   *  place of the hint (`notice`: the bonus couldn't open); `onOpenBonus`
   *  hears a tap on the gift while the bonus waits. */
  setTrailChrome(options: {
    hidden: boolean;
    collectorNoun?: string;
    notice?: string | null;
    onOpenBonus?: (() => void) | null;
  }): void {
    const trail = this.trailComponent();
    trail.setChrome(options);
    if (options.onOpenBonus !== undefined) trail.onOpenBonus = options.onOpenBonus;
  }

  /** Play the trail's pick-a-prize bonus; resolves with the trail as it
   *  stands when the board closes. Coins won fly into the coin counter. The
   *  road's gift square plays the same boxes under its own `words`. */
  playTrailBonus(
    trail: SlotTrailState,
    pick: (index: number) => Promise<{ trail: SlotTrailState; coins: number | null }>,
    words: { title?: string; finishLabel?: string } = {},
  ): Promise<SlotTrailState> {
    return this.trailComponent().playBonus(trail, pick, words);
  }

  /** The trail and its bonus as plain data (browser tests). */
  get trailUi(): { panel: TrailComponent['panelUi']; bonus: TrailComponent['bonusUi'] } {
    return { panel: this.trailChrome?.panelUi ?? null, bonus: this.trailChrome?.bonusUi ?? null };
  }

  /** Tap a part of the trail, or say where it is (browser tests). */
  tapTrail(part: TrailPart): boolean {
    return this.trailChrome?.tap(part) ?? false;
  }

  whereTrail(part: TrailPart): { x: number; y: number } | null {
    return this.trailChrome?.where(part) ?? null;
  }

  /** Open a bonus box, press a bonus button, or say where one is (browser tests). */
  tapTrailBox(index: number): boolean {
    return this.trailChrome?.tapBox(index) ?? false;
  }

  pressTrailBonus(label: string): boolean {
    return this.trailChrome?.pressBonus(label) ?? false;
  }

  whereTrailBonus(target: number | string): { x: number; y: number } | null {
    return this.trailChrome?.whereBonus(target) ?? null;
  }

  private trailComponent(): TrailComponent {
    if (!this.trailChrome) {
      this.trailChrome = new TrailComponent({
        stage: () => this.app?.stage ?? null,
        hud: () => this.hud,
        theme: this.theme,
        safeTop: () => safeAreaInsetTop(),
        boardTop: () => this.boardTop(),
        flyCoins: (from, amount) => this.flyCoinsToWallet(from, amount),
        // The server's wallet already holds a win still waiting to be
        // collected; the counter shows it only once it's collected.
        syncCoins: (coins) => this.syncWalletCoins(coins),
        cover: () => this.coverSlot(),
      });
      this.trailChrome.setCovered(this.covers > 0);
      if (this.lastCtx) this.trailChrome.layout(this.lastCtx);
    }
    return this.trailChrome;
  }

  /** Flash the slot's name over the reels as it opens; resolves when the
   *  flash has gone. */
  playNameFlash(name: string = this.theme.name): Promise<void> {
    // Not over a card or bonus already covering the slot (a run picked up
    // again as the slot opens).
    if (!this.app?.stage || this.covers > 0) return Promise.resolve();
    this.nameFlash ??= new NameFlash(() => {
      const alias = this.theme.assets?.scene?.columnBackground?.alias;
      return alias ? AssetLoader.shared.getTexture(alias) : null;
    });
    // Over the game, under anything opened after it.
    this.app.stage.addChild(this.nameFlash);
    if (this.lastCtx) this.placeNameFlash(this.lastCtx);
    return this.nameFlash.play(name);
  }

  /** The name flash as plain data (browser tests); null before the first. */
  get nameFlashUi(): NameFlashSnapshot | null {
    return this.nameFlash?.snapshot ?? null;
  }

  /** The name sits in the middle of the reels. */
  private placeNameFlash(ctx: SceneLayoutContext): void {
    if (!this.nameFlash) return;
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    this.nameFlash.layout((left + right) / 2, (ctx.machineTop + ctx.machineBottom) / 2, Math.min(ctx.width - 40, 560));
  }

  /** Play the Hi-Lo bonus; resolves with its payout (already flown into
   *  the wallet) or null if declined. */
  playHiLo(offer: HiLoOffer, api: HiLoApi, mode?: HiLoMode): Promise<number | null> {
    if (!this.hiloDialog) return Promise.resolve(null);
    this.app.stage.addChild(this.hiloDialog);
    if (this.lastCtx) this.hiloDialog.layout(this.lastCtx.width, this.lastCtx.height);
    const release = this.coverSlot();
    return this.hiloDialog.play(offer, api, mode).finally(release);
  }

  /** A new player's "Tap SPIN to play", in the notices' column under the
   *  top bar (so a gem toast or a banner goes under it, never over it). */
  setSpinHint(shown: boolean): void {
    if (!shown && !this.spinHint) return;
    if (!this.app?.stage) return;
    if (!this.spinHint) {
      this.spinHint = new SpinHint(this.boardFloor);
      // Over the game, under anything opened after it.
      this.app.stage.addChild(this.spinHint);
      if (this.lastCtx) this.placeSpinHint(this.lastCtx);
    }
    const hint = this.spinHint;
    if (shown && !this.spinHintSlot) {
      this.spinHintSlot = NoticeStack.shared.add(
        hint.boxHeight,
        ({ y, shown: allowed }) => {
          if (hint.destroyed) return;
          hint.setTop(y);
          hint.setAllowed(allowed);
        },
        () => (hint.destroyed ? null : hint.rect),
      );
    }
    void hint.setShown(shown).then(() => {
      // Its place goes once it has faded, so nothing moves up into it early.
      if (!shown && this.spinHint === hint && !hint.snapshot.visible) {
        this.spinHintSlot?.release();
        this.spinHintSlot = null;
      }
    });
  }

  /** Centred; its height keeps its place in the column. */
  private placeSpinHint(ctx: SceneLayoutContext): void {
    if (!this.spinHint) return;
    this.spinHint.layout(ctx.width / 2, ctx.width - 32);
    this.spinHintSlot?.setHeight(this.spinHint.boxHeight);
  }

  /** The first-visit hint as plain data (browser tests); null before it. */
  get spinHintUi(): SpinHintSnapshot | null {
    return this.spinHint?.snapshot ?? null;
  }

  /** What's alive in the background, as plain data (browser tests and
   *  screenshots read this); null when the theme has none or motion is
   *  reduced. */
  get ambientUi(): { butterflies: number; birds: number; patches: number; beams: number } | null {
    return this.ambientLife?.enabled ? this.ambientLife.snapshot : null;
  }

  /** The road's canvas chrome as plain data (browser tests read this,
   *  since nothing of it is in the DOM). */
  get roadUi(): {
    panel: boolean;
    run: { visible: boolean; title: string; counter: string; status: string; lives: number } | null;
    hilo: { open: boolean; card: string; line: string; sub: string; result: string; buttons: string[] } | null;
    titleCard: boolean;
    /** The last trail square's coins flying to the coin counter. */
    prizeFly: { coins: number; from: { x: number; y: number } } | null;
    /** Road coins not yet in the coin counter. */
    coinsOwed: number;
  } {
    return {
      panel: this.roadPanel?.visible === true,
      run: this.runBar?.snapshot ?? null,
      hilo: this.hiloDialog?.snapshot ?? null,
      titleCard: this.titleCard?.visible === true,
      prizeFly: this.roadPrizeFly,
      coinsOwed: this.roadCoinsOwed,
    };
  }

  /** The hold-and-spin bonus as plain data (browser tests read this,
   *  since nothing of it is in the DOM). */
  get holdAndSpinUi(): {
    board: boolean;
    spinsLeft: number;
    total: number;
    coins: number;
    end: { open: boolean; title: string; amount: string; line: string; buttons: string[] } | null;
    titleCard: boolean;
  } {
    const board = this.holdAndSpinComponent?.snapshot ?? { board: false, spinsLeft: 0, total: 0, coins: 0 };
    return { ...board, end: this.bonusWinDialog?.snapshot ?? null, titleCard: this.titleCard?.visible === true };
  }

  /** Press the hold-and-spin end board's button by its label (browser tests). */
  pressBonus(label: string): boolean {
    return this.bonusWinDialog?.press(label) ?? false;
  }

  /** Press a Hi-Lo button by its label (browser tests). */
  pressHiLo(label: string): boolean {
    return this.hiloDialog?.press(label) ?? false;
  }

  /** Where coins paid by the run fly from: the middle of the run bar. */
  get runCoinSource(): { x: number; y: number } {
    return this.runBar?.centre ?? { x: (this.lastCtx?.width ?? 0) / 2, y: 120 };
  }

  private layoutRoadChrome(ctx: SceneLayoutContext): void {
    // Closed already: a host's last word about the slot can come after.
    if (this.roadPanel?.destroyed) return;
    const chrome = this.roadChrome;
    if (!chrome || !this.roadPanel || !this.runBar || this.roadNow.road === null || this.roadChromeHidden) {
      // No road panel on screen: the hold-and-spin board sits just above
      // the reels instead.
      this.holdAndSpinComponent?.setBoardSlot(null);
      if (!chrome || !this.roadPanel || !this.runBar) return;
    }
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    const width = Math.min(right - left, chrome.maxWidth, ctx.width - 8);
    const x = Math.max(4, Math.min(ctx.width - 4 - width, (left + right) / 2 - width / 2));
    const expanded = chrome.mode === 'chip' && this.roadExpanded;
    this.roadPanel.setLayout(expanded ? 'full' : chrome.mode, x, chrome.top, width, expanded ? 180 : chrome.height);
    this.runBar.setLayout(x, chrome.top, width, Math.max(chrome.height, 96));
    if (this.roadNow.road !== null && !this.roadChromeHidden) {
      // The board takes the panel's place during the bonus, and the room
      // down to the reels when the panel is only a slim strip (small phones).
      const room = Math.min(120, ctx.machineTop - 10 - chrome.top);
      this.holdAndSpinComponent?.setBoardSlot({ x, y: chrome.top, width, height: Math.max(chrome.height, room) });
    }
    this.raiseRoadPanel(expanded);
  }

  /** A phone's panel opened from its chip lies over the top of the reels,
   *  where the HUD pins the free-spin chip: open, it goes above the HUD (or
   *  the chip covers its lamps); folded, back just under it. Pop-ups added
   *  above the HUD stay above it. */
  private raiseRoadPanel(over: boolean): void {
    const stage = this.app.stage;
    const panel = this.roadPanel;
    if (!panel || !this.hud || panel.parent !== stage) return;
    const above = stage.getChildIndex(panel) > stage.getChildIndex(this.hud);
    if (above === over) return;
    stage.removeChild(panel);
    stage.addChildAt(panel, stage.getChildIndex(this.hud) + (over ? 1 : 0));
  }

  /** One of the road's own pieces (`road_ring`, `road_hub`, `road_life`,
   *  `road_path`) by the alias the theme gives it: a Wheel Run slot shows
   *  its world road's, under that road's alias. */
  private roadPiece(key: string): Texture | null {
    const alias = this.theme.assets?.road?.textures?.[key]?.alias;
    return alias ? AssetLoader.shared.getTexture(alias) : null;
  }

  private roadTexture(name: string): Texture | null {
    const loader = AssetLoader.shared;
    switch (name) {
      case 'lamp':
        return loader.getTexture('road:lamp_lens');
      case 'bezel':
        return loader.getTexture('road:light_bezel');
      case 'cobbles':
        return loader.getTexture('road:path_strip');
      case 'path':
        // The world's own road surface; none on Forest or the kingdom's
        // other slots.
        return this.roadPiece('road_path');
      case 'potSilver':
        return loader.getTexture('road:pot_silver');
      case 'potGold':
        return loader.getTexture('road:pot_gold');
      case 'lock':
        return loader.getTexture('road:lock');
      case 'life':
        return this.roadPiece('road_life');
      case 'gift':
        return loader.getTexture('shared:icon:gift');
      case 'coin':
        return loader.getTexture('shared:icon:coin');
      case 'gem':
        return loader.getTexture('shared:icon:gem');
      case 'spin':
        return loader.getTexture('shared:icon:spin');
      case 'card':
        return loader.getTexture('card:face');
      case 'collector': {
        const alias = this.theme.assets?.slot?.['cherry']?.alias;
        return alias ? loader.getTexture(alias) : null;
      }
      case 'floor': {
        const alias = this.theme.assets?.scene?.columnBackground?.alias;
        return alias ? loader.getTexture(alias) : null;
      }
      default:
        // The road's and the game's own pieces by alias (friends' faces,
        // games' lamp icons).
        return name.startsWith('road:') || name.startsWith('shared:') ? loader.getTexture(name) : null;
    }
  }

  /** Say what the side lights are for, near where they were tapped (or
   *  under the panel the first time they light). */
  private explainLights(at: { x: number; y: number } | null): void {
    const ctx = this.lastCtx;
    if (!this.roadPanel || !ctx) return;
    const point = at ?? { x: (ctx.machineLeft ?? 0) + 40, y: ctx.machineTop + 20 };
    this.roadPanel.explainAt(LIGHTS_EXPLAINER, this.roadPanel.toLocal(point));
  }

  /** Take the board away and go back to the reels. */
  endBoardRun(): void {
    this.landmarkRoad?.setEnabled(true);
    this.boardRun?.end();
    this.sideLights?.setMode('ready');
    this.setSpinMode('spin');
  }

  private handleBoardButton(mode: SpinMode): void {
    if (mode === 'stop') this.onBoardStopPressed?.();
    else if (mode === 'collect' || mode === 'done') this.onBoardCollectPressed?.();
  }

  /** Phase 3: put the story's layer over the slot, under the paytable, and
   *  lay it out with the scene. */
  mountStory(layer: Container & SceneComponent): void {
    if (!this.contentBuilt) {
      // The host gets the scene at construction; the stage is empty until
      // it's built, and a layer added now would end up under everything.
      this.storyWaiting = layer;
      return;
    }
    if (!this.app?.stage) return;
    const stage = this.app.stage;
    const at = this.paytable && this.paytable.parent === stage ? stage.getChildIndex(this.paytable) : stage.children.length;
    stage.addChildAt(layer, at);
    this.storyLayer = layer;
    if (this.lastLayout) layer.layout(this.lastLayout);
  }

  /** Resolves once the scene has built its content (its textures, such as
   *  the reel floor, are loaded by then). */
  whenBuilt(): Promise<void> {
    return this.builtPromise;
  }

  /** The slot's reel floor texture (for boards drawn over the slot). */
  floorTexture(): Texture | null {
    const alias = this.theme.assets?.scene?.columnBackground?.alias;
    return alias ? AssetLoader.shared.getTexture(alias) : null;
  }

  setWalletAnchors(
    anchors:
      | { coin: { x: number; y: number }; gem: { x: number; y: number } }
      | null,
  ): void {
    this.hud?.setWalletAnchors(anchors);
  }

  /** Info-button tap handler. Standalone slot just toggles the
   *  paytable. Multiplayer Reel Race (when {@link itemsCatalog}
   *  exists) opens whichever modal was last visible OR the paytable
   *  by default — tab swapping happens INSIDE the dialog via the
   *  pill row at the top, not via repeated info-button taps.
   *  Producer brief 2026-05-15. */
  private cycleInfoModal(): void {
    if (!this.paytable) return;
    const open = this.paytable.isVisible() || this.itemsCatalog?.isVisible() === true;
    if (open) {
      // Anything open: close it.
      this.paytable.hide();
      this.itemsCatalog?.hide();
      this.infoClosed();
      return;
    }
    // Closed: open the paytable (the Reel Race's Items is a tab on it).
    this.paytable.show();
    this.infoOpened();
  }

  /** Lets the slot go again once the paytable or items board closes. */
  private infoCover: (() => void) | null = null;

  /**
   * The paytable or items board opened: it draws over everything on the
   * canvas (the HUD, the road panel), and the overlays above the canvas
   * step aside, so nothing covers it (producer 2026-10-14).
   */
  private infoOpened(): void {
    for (const board of [this.paytable, this.itemsCatalog]) {
      if (board?.isVisible()) this.app.stage.addChild(board);
    }
    if (!this.infoCover) this.infoCover = this.coverSlot();
    this.onInfoModalToggle?.(true);
  }

  private infoClosed(): void {
    if (this.paytable?.isVisible() || this.itemsCatalog?.isVisible()) return;
    this.infoCover?.();
    this.infoCover = null;
    this.onInfoModalToggle?.(false);
  }

  /** Wire the multiplayer tab strip on both info dialogs. Each
   *  dialog gets two pills (Paytable + Items); tapping the inactive
   *  pill hides this dialog and shows the other. Producer brief
   *  2026-05-15 — tabs replace the previous "tap info twice"
   *  cycling, which was undiscoverable. The dim-tap on either dialog
   *  also fires the host {@code onInfoModalToggle(false)} so the
   *  React overlays come back. */
  private configureMultiplayerTabs(): void {
    if (!this.paytable || !this.itemsCatalog) return;
    const paytable = this.paytable;
    const itemsCatalog = this.itemsCatalog;
    paytable.setTabs([
      { label: 'PAYTABLE', active: true, onTap: () => {} },
      {
        label: 'ITEMS',
        active: false,
        onTap: () => {
          paytable.hide();
          itemsCatalog.show();
          this.infoOpened();
        },
      },
    ]);
    itemsCatalog.setTabs([
      {
        label: 'PAYTABLE',
        active: false,
        onTap: () => {
          itemsCatalog.hide();
          paytable.show();
          this.infoOpened();
        },
      },
      { label: 'ITEMS', active: true, onTap: () => {} },
    ]);
    // Both dialogs close on dim-tap. Wire a hide listener via the
    // visibility flag so the React host learns the modal is gone
    // even when the close path skips cycleInfoModal.
    paytable.onCloseRequested = () => this.infoClosed();
    itemsCatalog.onCloseRequested = () => this.infoClosed();
  }

  /** Build the source-cell list used by the take-win animation —
   *  every line cell that contributed to a settled payline (the first
   *  {@code matchCount} positions of each winning {@link WinLine}),
   *  plus every scatter cell. Positions are converted from grid
   *  coordinates ((col, row)) to stage-local pixel coordinates via
   *  {@link SlotMachine#cellCenter} + the machine's own (x, y). */
  private collectWinningCellPositions(
    settlement: SpinSettlement,
  ): Array<{ x: number; y: number }> {
    if (!this.machine) return [];
    const positions: Array<{ x: number; y: number }> = [];
    const linesById = new Map(this.winLines.map((l) => [l.id, l]));
    // Match the machine's display scale so machine-local cellCenter
    // coords land at the right stage position. Reels are children of
    // {@code this.machine}, so the machine's own (x, y, scale) is the
    // local→stage transform.
    const mx = this.machine.x;
    const my = this.machine.y;
    const scale = this.machine.scale.x;
    for (const s of settlement.settlements) {
      const line = linesById.get(s.lineId);
      if (!line) continue;
      const winningCells = line.positions.slice(0, s.matchCount);
      for (const cell of winningCells) {
        const c = this.machine.cellCenter(cell.col, cell.row);
        positions.push({ x: mx + c.x * scale, y: my + c.y * scale });
      }
    }
    // Scatter positions — direct (col, row) from settlement.
    const scatterCells = settlement.scatter?.positions ?? [];
    for (const cell of scatterCells) {
      const c = this.machine.cellCenter(cell.col, cell.row);
      positions.push({ x: mx + c.x * scale, y: my + c.y * scale });
    }
    return positions;
  }

  /** Restore "ready for a fresh spin" state: visible spin button, enabled
   * bet selector, hold-tap re-enabled, hint label restored. Called after a
   * no-win spin, after the gamble flow exits, and after a failed spin. */
  private exitToIdle(): void {
    if (!this.hud || !this.machine) return;
    this.spinning = false;
    this.hud.setSpinning(false);
    this.hud.setSpinButtonVisible(true);
    this.gambleFlow?.hideButtons();
    // Inline GAMBLE medallion is reset by exitToIdle so a fresh spin
    // starts with a clean HUD (no stale gamble offer from a prior win).
    this.hud.setGambleButtonVisible(false);
    this.hud.setMaxAffordable(this.model.balance);
    this.machine.setHoldInteractive(true);
    this.model.clampBetToAffordable();
    this.hud.refreshHint();
    if (this.onIdle) {
      try { this.onIdle(); }
      catch (err) { console.warn('[SlotDemoScene] onIdle threw; ignoring', err); }
    }
  }

  /** True between spins: nothing spinning, celebrating or paying out. */
  get idle(): boolean {
    return !this.spinning && !this.pendingServerBonus;
  }

  /** True while a board is up on the slot (a road card, a chapter's card or
   *  its moment, Hi-Lo, a friend saying hello, a title card, a mini-game, a
   *  story beat or card, or any other board dialog): the app's own boards
   *  wait for it. */
  get boardOpen(): boolean {
    return (
      (this.landmarkRoad?.blocking ?? false) ||
      this.titleCard?.visible === true ||
      this.hiloDialog?.visible === true ||
      (this.miniGames?.busy ?? false) ||
      ((this.storyLayer as { busy?: boolean } | null)?.busy ?? false) ||
      (this.app?.stage?.children.some((c) => c instanceof BoardDialog && c.visible) ?? false)
    );
  }

  /** True when a win may be offered the gamble: the host allows it (the
   *  Reel Race does not) and the player has not switched it off in
   *  Settings. Read on every win, so a Settings change applies to the
   *  next one without rebuilding the scene. */
  private gambleOffered(): boolean {
    return this.gambleEnabled && this.gambleFlow !== null && (this.profile?.gambleEnabled ?? true);
  }

  /** The server decides a gamble on a server spin's win: it picks the colour
   *  and moves the wallet by the pot. The local balance is set so that
   *  balance + pending winnings is the server's wallet again, which is what
   *  collecting the pot then shows. Null (a local draw) for a local spin. */
  private serverGamble(): GambleDecide | null {
    const gambleFn = this.gambleFn;
    const spinId = this.lastServerSpinId;
    if (!this.serverSpins || !gambleFn || !spinId) return null;
    return async (picked) => {
      const r = await gambleFn({ spinId, step: this.model.gambleStreak + 1, guess: picked });
      this.dropRoadCoins();
      this.model.setBalance(r.coins - r.potAfter, 'session-grant');
      return { actualColor: r.colour, winningsAfter: r.potAfter };
    };
  }

  /** Inline GAMBLE medallion tap handler (2026-06-04). Hides the HUD's
   *  inline button while the card-pick overlay runs, fires the overlay
   *  via {@link GambleFlowComponent#openGambleOverlay}, then either re-
   *  shows the inline button (gamble won and pendingWinnings carries
   *  to a re-offer) or stays hidden (gamble lost → flow exits + idle). */
  private async handleInlineGamble(): Promise<void> {
    if (!this.gambleFlow || !this.hud || !this.gambleOffered()) return;
    this.hud.setGambleButtonVisible(false);
    await this.gambleFlow.openGambleOverlay();
    // Re-show the inline GAMBLE if the player WON the gamble round
    // (handleGamble re-stages pendingWinnings at 2× the previous
    // amount). Lose path zeros pendingWinnings and routes through
    // exitToIdle which hides the button itself; cap path also drops
    // to 0 via the auto-collect. So a pending-winnings check is the
    // single correct gate.
    if (this.hud && this.model.pendingWinnings > 0) {
      this.hud.setGambleButtonVisible(true);
    }
  }
}

/** "Out of coins" with when the free refill comes. */
function outOfCoinsText(nextRefillAt: number | null): string {
  if (nextRefillAt === null) return 'Out of coins. Free coins are on the way.';
  const mins = Math.max(1, Math.ceil((nextRefillAt - Date.now()) / 60_000));
  const wait = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  return `Out of coins. Free coins in ${wait}.`;
}

function describeWin(
  settlement: SpinSettlement,
  freeSpinsGranted: number,
  wasFreeSpin: boolean,
): string {
  const prefix = wasFreeSpin ? '🎯 FREE SPIN · ' : '';
  let body: string;
  if (settlement.scatter !== null && settlement.settlements.length === 0) {
    body = `${settlement.scatter.scatterCount}× scatter → +${settlement.totalWinnings}`;
  } else if (settlement.settlements.length === 1) {
    const s = settlement.settlements[0]!;
    body = `${s.matchSymbolId} × ${s.matchCount} on ${s.lineId} → +${settlement.totalWinnings}`;
  } else {
    body = `${settlement.settlements.length} winning lines → +${settlement.totalWinnings}`;
  }
  const trigger = freeSpinsGranted > 0 ? `   ✨ +${freeSpinsGranted} FREE SPINS` : '';
  return `${prefix}${body}${trigger}`;
}
